import {
  pgTable,
  text,
  varchar,
  timestamp,
  uuid,
  date,
  time,
  jsonb,
  pgEnum,
  boolean,
  integer,
  decimal,
  serial,
  primaryKey,
  unique,
  bigint,
  real,
  vector,
  index,
  uniqueIndex,
  customType,
  AnyPgColumn,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { z } from "zod";

// Define the platform enum
export const SubscriptionPlatform = pgEnum("subscription_platform", [
  "stripe",
  "apple",
  "google",
]);

export const SubscriptionPlatformType = z.enum(SubscriptionPlatform.enumValues);

export const SubscriptionStatus = pgEnum("subscription_status", [
  "active",
  "past_due",
  "canceled",
  "incomplete",
  "incomplete_expired",
  "trialing",
  "unpaid",
  "paused",
]);

export const SubscriptionStatusType = z.enum(SubscriptionStatus.enumValues);

export const EvidenceVendor = pgEnum("evidence_vendor", [
  "axon",
  "getac",
  "lenslock",
]);

export type EvidenceVendorType = (typeof EvidenceVendor.enumValues)[number];

export const ExternalAuthProvider = pgEnum("auth_provider", [
  "apple",
  "google",
  "email",
  "saml",
  "oidc",
]);

export const ExternalAuthProviderType = z.enum(ExternalAuthProvider.enumValues);

export const MfaMethod = pgEnum("mfa_method", ["totp", "email", "webauthn"]);
export const MfaMethodType = z.enum(MfaMethod.enumValues);

export const PreferenceDateFormat = pgEnum("date_format", [
  "MMMM DD YYYY",
  "MM/DD/YYYY",
  "DD/MM/YYYY",
  "DD MMM YY",
  "DD MMMM YYYY",
]);

export const PreferenceDateFormatType = z.enum(PreferenceDateFormat.enumValues);

export const CallSignFormat = pgEnum("call_sign_format", [
  "before_name", // "1-1 (Officer Smith)"
  "after_badge_dash", // "Officer Smith #406 - 1-1"
  "after_name_parentheses", // "Officer Smith #406 (1-1)"
]);

export const CallSignFormatType = z.enum(CallSignFormat.enumValues);

export const CallSignMention = pgEnum("call_sign_mention", [
  "first_mention",
  "every_mention",
]);

export const CallSignMentionType = z.enum(CallSignMention.enumValues);

export const PreferenceTimeFormat = pgEnum("time_format", [
  "military_time",
  "standard_time",
]);

export const PreferenceTimeFormatType = z.enum(PreferenceTimeFormat.enumValues);

export const PreferencePerspective = pgEnum("perspective", [
  "first_person",
  "third_person",
]);

export const PreferencePerspectiveType = z.enum(
  PreferencePerspective.enumValues
);

export const PreferenceStatuteState = pgEnum("statute_state", [
  "",
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
  "District of Columbia",
]);

export const PreferenceStatuteStateType = z.enum(
  PreferenceStatuteState.enumValues
);

export const SubjectReferenceStyle = pgEnum("subject_reference_style", [
  "full_name_throughout",
  "last_name_after_first_mention",
  "first_name_after_first_mention",
  "role_after_first_mention",
  "role_with_last_name_after_first_mention",
  "role_with_first_name_after_first_mention",
]);

export const SubjectReferenceStyleType = z.enum(
  SubjectReferenceStyle.enumValues
);

export type ReviewMetadata = {
  last_prompt_shown_at?: string;
};

export type PlatformReviewStatus = {
  ios?: ReviewMetadata;
  android?: ReviewMetadata;
  web?: ReviewMetadata;
};

export type ChecklistItem = {
  id: string;
  text: string;
  required: boolean;
  category?: string;
  order: number; // For maintaining display order
  description?: string; // For additional context or clarification
  additionalNotesEnabled?: boolean; // Allow officers to add notes when checking this item
};

// Report template content — each section is plain text with {{AI Placeholder}} markers
export type ReportTemplateContent = {
  header: string;
  footer: string;
  body: string;
};

// Academy (in-app living docs) progress: lesson ids → ISO timestamps.
export type AcademyProgress = {
  viewed?: Record<string, string>;
  completed?: Record<string, string>;
  lastVisitedAt?: string;
};

export type ChecklistData = {
  items: {
    [itemId: string]: {
      checked: boolean;
      checked_at?: string;
      additionalNotes?: string; // Notes added by officers when checking items
    };
  };
  started_at?: string;
  completed_at?: string;
};

export const users = pgTable("users", {
  id: uuid("id").primaryKey().defaultRandom(),
  agency_id: uuid("agency_id").references(() => agencies.id),
  stripe_customer_id: varchar("stripe_customer_id", { length: 255 }),
  name: text("name"),
  email: varchar("email", { length: 255 }).notNull().unique(),
  password: varchar("password", { length: 255 }),
  badgeID: varchar("badge_id", { length: 255 }),
  department: varchar("department", { length: 255 }),
  avatarUrl: text("avatar_url"),
  external_auth_id: varchar("external_auth_id", { length: 255 }),
  external_auth_provider: ExternalAuthProvider("external_auth_provider"),
  reset_password_token: varchar("reset_password_token", { length: 255 }),
  reset_password_expires: timestamp("reset_password_expires"),
  platform_review_status: jsonb("platform_review_status")
    .$type<PlatformReviewStatus>()
    .default({}),
  seen_tutorials: jsonb("seen_tutorials").$type<string[]>().default([]),
  academy_progress: jsonb("academy_progress").$type<AcademyProgress>().default({}),
  mfa_setup_required_at: timestamp("mfa_setup_required_at"),
  // Staff "Send survey" to this one officer: while this is in the future
  // the officer is prompted regardless of trial state, exactly like
  // agencies.survey_open_until does for the whole department.
  survey_open_until: timestamp("survey_open_until", { withTimezone: true }),
  created_at: timestamp("created_at").defaultNow(),
  updated_at: timestamp("updated_at").defaultNow(),
  deleted_at: timestamp("deleted_at", { withTimezone: true }),
});

export type User = typeof users.$inferSelect;
export type NewUser = typeof users.$inferInsert;

export const reportPreferences = pgTable("report_preferences", {
  id: uuid("id").primaryKey().defaultRandom(),
  userId: uuid("user_id").references(() => users.id, { onDelete: "cascade" }),
  agencyId: uuid("agency_id").references(() => agencies.id),
  badge_number: boolean("badge_number").default(true),
  name: boolean("name").default(true),
  top_sheet_information: boolean("top_sheet_information").default(false),
  topic_headings: boolean("topic_headings").default(false),
  abbreviate_rank: boolean("abbreviate_rank").default(true),
  capitalize_names: boolean("capitalize_names").default(false),
  capitalize_rank: boolean("capitalize_rank").default(false),
  day_of_week: boolean("day_of_week").default(true),
  date_format: PreferenceDateFormat("date_format").default("MMMM DD YYYY"),
  time_format: PreferenceTimeFormat("time_format").default("military_time"),
  perspective: PreferencePerspective("perspective").default("first_person"),
  subject_reference_style: SubjectReferenceStyle(
    "subject_reference_style"
  ).default("role_with_last_name_after_first_mention"),
  header: text("header"),
  footer: text("footer"),
  additional_prompt: text("additional_prompt"),
  rank: text("rank"),
  include_call_sign: boolean("include_call_sign").default(false),
  call_sign: text("call_sign"),
  call_sign_format: CallSignFormat("call_sign_format").default("before_name"),
  call_sign_mention: CallSignMention("call_sign_mention").default(
    "first_mention"
  ),
  statute_state: PreferenceStatuteState("statute_state").default(""),
  common_spellings: text("common_spellings").array(),
  default_template_id: uuid("default_template_id").references(() => reportTemplates.id, { onDelete: "set null" }),
  created_at: timestamp("created_at").defaultNow(),
  updated_at: timestamp("updated_at").defaultNow(),
});

export type ReportPreferences = typeof reportPreferences.$inferSelect;
export type NewReportPreferences = typeof reportPreferences.$inferInsert;

export const subscriptions = pgTable("subscriptions", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").references(() => users.id, { onDelete: "cascade" }),
  agency_id: uuid("agency_id").references(() => agencies.id),
  platform: SubscriptionPlatform("platform")
    .notNull()
    .default(SubscriptionPlatformType.enum.stripe),
  platform_subscription_id: varchar("platform_subscription_id", {
    length: 255,
  }),
  receipt_data: text("receipt_data"),
  receipt_verified_at: timestamp("receipt_verified_at"),
  status: SubscriptionStatus("status").notNull(),
  current_period_end: timestamp("current_period_end").notNull(),
  cancel_at_period_end: boolean("cancel_at_period_end").default(false),
  is_trial: boolean("is_trial").default(false),
  payment_captured: boolean("payment_captured").default(false),
  price_id: varchar("price_id", { length: 255 }),
  currency: varchar("currency", { length: 3 }),
  interval: varchar("interval", { length: 50 }),
  metadata: jsonb("metadata"),
  created_at: timestamp("created_at").defaultNow(),
  updated_at: timestamp("updated_at").defaultNow(),
});

export type Subscription = typeof subscriptions.$inferSelect;
export type NewSubscription = typeof subscriptions.$inferInsert;

export const subscription_invoices = pgTable("subscription_invoices", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").references(() => users.id, { onDelete: "cascade" }),
  agency_id: uuid("agency_id").references(() => agencies.id),
  subscription_id: uuid("subscription_id")
    .notNull()
    .references(() => subscriptions.id, { onDelete: "cascade" }),
  platform: SubscriptionPlatform("platform")
    .notNull()
    .default(SubscriptionPlatformType.enum.stripe),
  platform_invoice_id: varchar("platform_invoice_id", { length: 255 }).unique(),
  amount: decimal("amount", { precision: 10, scale: 2 }),
  status: varchar("status", { length: 50 }).notNull(),
  period_start: timestamp("period_start").notNull(),
  period_end: timestamp("period_end").notNull(),
  metadata: jsonb("metadata"),
  created_at: timestamp("created_at").defaultNow(),
});

export type SubscriptionInvoice = typeof subscription_invoices.$inferSelect;
export type NewSubscriptionInvoice = typeof subscription_invoices.$inferInsert;

export const CaseStatus = pgEnum("case_status", [
  "active",
  "archived",
  "discarded",
  "processing",
  "error",
  "checklist",
]);

export const CaseStatusType = z.enum(CaseStatus.enumValues);

export const FileMediaType = pgEnum("file_media_type", [
  "audio",
  "image",
  "video",
  "document",
]);

export const FileMediaTypeType = z.enum(FileMediaType.enumValues);

export const FileProcessingStatus = pgEnum("file_processing_status", [
  "pending",
  "uploading",
  "processing",
  "completed",
  "failed",
]);

export const FileProcessingStatusType = z.enum(FileProcessingStatus.enumValues);

export type StatuteType = {
  code: string;
  name: string;
  likelihood: number;
  description: string;
  grade: string;
  source: string;
  // Set when the statute came from an agency-uploaded document (source
  // "Agency: <title> § <section>"); links the Statutes panel to the page.
  documentId?: string;
  sectionId?: string;
  page?: number;
};

// A policy/statute section retrieved for a case: what the draft was given,
// what the policy check read, or what Identify Statutes used. Snapshot fields
// (title/excerpt) keep old reports readable after a document is deleted.
export type PolicyReference = {
  documentId: string;
  sectionId: string;
  documentTitle: string;
  docType: "statute" | "policy" | "other";
  sectionNumber: string | null;
  heading: string | null;
  pageStart: number | null;
  pageEnd: number | null;
  excerpt: string;
  how: ("draft" | "check" | "statutes")[];
  score?: number;
};

// PII / sensitive items the model (or user) flagged for redaction on export.
// `text` is matched verbatim against the report HTML at highlight time.
// `category` drives the badge color in the sidebar; `custom` is true for items
// the user added by hand instead of items the AI surfaced.
// `tag` is a short reader-facing label shown inside the black bar on export
// (e.g. "suspect", "victim", "address"). `statute` + `justification` carry the
// statutory ground for withholding the item (e.g. "16 M.R.S. §804(3)" /
// "Constitutes an invasion of privacy") and feed the optional footnotes
// section on exported documents.
export type RedactionType = {
  id: string;
  text: string;
  category:
    | "Name"
    | "Address"
    | "Phone"
    | "Email"
    | "SSN"
    | "DOB"
    | "License Plate"
    | "Driver's License"
    | "Vehicle"
    | "Juvenile"
    | "Medical"
    | "Financial"
    | "Other";
  reason?: string;
  tag?: string;
  statute?: string;
  justification?: string;
  custom?: boolean;
};

export type AudioMetadataType = {
  audioType: string;
  fileType?: string;
  fileSize?: string;
  source?: string;
  method?: string;
  speakerMap?: {
    [speakerId: string]: {
      name: string;
      initials: string;
    };
  };
  supplementalAudio?: {
    audioUrl?: string;
    fileSize?: string;
    fileType?: string;
    type?: string;
    transcriptionUrl?: string;
  };
  // Controls the multifile completion handler — see AudioFileMetadata.intent for details.
  // "generating_changes" is a sub-state of "add_to_report" set by the atomic
  // intent claim once the batch is fully transcribed and Bedrock is running.
  intent?: "new_case" | "regenerate" | "add_to_report" | "generating_changes";
  fileCount?: number;
};

// Type-specific metadata structures for case_files table
export type AudioFileMetadata = {
  audioType?: "narrative" | "interaction";
  duration?: number;
  transcriptionUrl?: string;
  speakerMap?: {
    [speakerId: string]: {
      name: string;
      initials: string;
    };
  };
  context?: string; // Additional context for this audio
  // 'upload' | 'recording' | 'dictation' | 'live_translation' (mobile two-way
  // spoken translation: the transcript is written by the server, not a Lambda)
  source?: string;
  method?: string;
  // Transcribe language hints for uploads; for live_translation the pair
  // [officerLang, subjectLang] as BCP-47 tags.
  languages?: string[];
  uploadOptions?: any;
  // The intent of the upload — controls what happens after all files are transcribed.
  // "new_case" (default): trigger Bedrock multifile-initial to generate the report.
  // "regenerate": triggered by re-generating the narrative on an existing case.
  // "add_to_report": files belong to a pending Add to Report batch.
  // "generating_changes": sub-state of add_to_report, set by the completion
  //   handler's atomic claim once Bedrock is running for the batch.
  intent?: "new_case" | "regenerate" | "add_to_report" | "generating_changes";
  // Set when the officer asked for the footage itself to be analyzed. The
  // row keeps media_type "audio" so the transcript pipeline is untouched.
  video?: VideoAnalysisMetadata;
};

export type ImageFileMetadata = {
  width?: number;
  height?: number;
  caption?: string;
  description?: string;
  ocrText?: string; // Extracted text from image
  location?: string; // Where in the report this image should appear
  isEvidence?: boolean;
  timestamp?: string; // When photo was taken
  exifData?: any; // EXIF metadata from the image
};

export type VideoFileMetadata = {
  duration?: number;
  width?: number;
  height?: number;
  thumbnailUrl?: string;
  transcriptionUrl?: string; // If video has audio
  hasAudio?: boolean;
  description?: string;
  context?: string;
};

// ---- Video analysis (shared by case footage and investigation video) ------

// What the visual passes look for. Colors on the marker bar key off this.
export const VIDEO_EVENT_KINDS = [
  "arrival",
  "contact",
  "command",
  "compliance",
  "resistance",
  "use_of_force",
  "search",
  "seizure",
  "handcuffing",
  "injury",
  "weapon",
  "vehicle",
  "medical",
  "property",
  "text_read",
  "other",
] as const;
export type VideoEventKind = (typeof VIDEO_EVENT_KINDS)[number];

// One entry of the visual event log a video's detail pass produces.
// Times are milliseconds into the video; frame_ts are the seconds of the
// extracted stills that support the event.
export type VideoEvent = {
  id: string; // stable within the file: `${t_start_ms}-${kind}-${n}`
  t_start_ms: number;
  t_end_ms: number;
  kind: VideoEventKind;
  description: string;
  actors?: { label: string; role: "officer" | "subject" | "witness" | "other"; appearance?: string }[];
  objects?: string[];
  confidence: number; // 0-1
  frame_ts: number[];
  text_read?: string; // plate / sign / label text when kind is text_read
  region?: { x: number; y: number; w: number; h: number }; // normalized box in frame_ts[0]
  not_visible?: boolean; // the camera was obstructed for part of the span
};

export type VideoTriageMinute = {
  minute: number;
  scene: string;
  people_count?: number;
  camera_state: "normal" | "obstructed" | "dark" | "holstered" | "off_body";
  activity: "none" | "low" | "high";
};

export type VideoSegmentOfInterest = {
  start_ms: number;
  end_ms: number;
  reason: string;
  priority: 1 | 2 | 3;
};

// The frames Lambda's manifest, written beside the stills.
export type VideoFramesManifest = {
  version: 1;
  fps: number; // base extraction rate (1)
  width: number;
  height: number;
  durationSec: number;
  hasAudio: boolean;
  frameWidth: number; // the extracted still's width (1024)
  frames: { t: number; key: string }[]; // sorted by t; dense (4 fps) where motion was high
  sheets: { minute: number; key: string; tiles: number[] }[]; // contact sheets, tile t values
  motion: number[]; // per-second 0-1 motion score
  proxyKey?: string; // browser-playable mp4
  audioKey?: string; // extracted m4a when the Lambda made one
};

// What the video pipeline writes onto the owning row's metadata:
// case_files.metadata.video (report footage) or
// investigation_files.metadata.video (investigation video).
export type VideoAnalysisMetadata = {
  bucket: string;
  prefix: string; // frames/{...}/ prefix in the bucket
  manifestKey: string;
  proxyKey?: string;
  durationSec?: number;
  width?: number;
  height?: number;
  hasAudio?: boolean;
  frameCount?: number;
  status: "pending" | "extracting" | "analyzing" | "completed" | "failed";
  error?: string;
  // Live progress while analyzing, written on every pass callback.
  progress?: VideoProgressState;
  mode?: "verify" | "enrich"; // cases only
  analyzeAll?: boolean; // detail pass over every minute, not just flagged segments
  watchFor?: string; // detective's "look for" brief (investigations)
  eventsKey?: string; // events.json beside the manifest
  eventCount?: number;
  triage?: { minutes: VideoTriageMinute[]; segments: VideoSegmentOfInterest[] };
  conditions?: { lighting?: string; weather?: string; location_type?: string };
  summary?: string;
  documentType?: string; // bodycam | surveillance | cellphone | dashcam | interview_room | other
  analysisVersion?: number;
  analyzedAt?: string;
  tokensIn?: number;
  tokensOut?: number;
  originalPendingDelete?: boolean; // cases: original video still in S3 until transcription lands
  attachedStills?: number[]; // cases: frame seconds the officer attached to the export
};

export type VideoProgressState = {
  stage: "triage" | "detail" | "summary" | "check";
  done: number;
  total: number;
  pct: number; // 0-100 across the whole analysis
  updatedAt: string;
};

// One finding of the footage check (cases): the narrative vs the event log.
export type FootageFinding = {
  id: string;
  kind: "unsupported_claim" | "omitted_event" | "timing_conflict" | "contradiction";
  severity: 1 | 2 | 3 | 4 | 5;
  narrative_quote?: string;
  footage: { t_start_ms: number; t_end_ms: number; description: string; frame_ts: number[] };
  suggested_edit?: string;
  explanation: string;
  suggestionId?: string; // the suggestions row it was posted as
};

export type DocumentFileMetadata = {
  pageCount?: number;
  extractedText?: string;
  documentType?: string; // 'pdf' | 'word' | 'excel' | etc.
  description?: string;
  context?: string;
};

// JSONB shape for `cases.pending_suggestions_changes`. Set when an "Add to Report"
// audio batch finishes transcribing and Bedrock returns discrete changes; cleared
// when the user accepts or discards them.
export type PendingSuggestionsChanges = {
  changes: any[]; // SuggestionChange[] from client/src/components/Editor/SuggestionsReview.tsx
  fileIds: string[]; // case_files.id refs (for cleanup on discard)
  createdAt: string; // ISO timestamp
  error?: string; // Set if Bedrock failed; banner shows retry button
  droppedInvalid?: number; // count of Bedrock changes dropped because their anchor text wasn't found in the report
};

export const cases = pgTable("cases", {
  id: uuid("id").primaryKey().defaultRandom(),
  userId: uuid("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  caseNumber: varchar("case_number", { length: 50 }),
  incidentDate: date("incident_date"),
  incidentTime: time("incident_time"),
  status: CaseStatus("status").default("processing"),
  error: text("error"),
  content: jsonb("content"),
  audioUrl: text("audio_url"),
  audioMetadata: jsonb("audio_metadata").$type<AudioMetadataType>(),
  transcriptionUrl: text("transcription_url"),
  statutes: jsonb("statutes").$type<StatuteType[]>().default([]),
  redactions: jsonb("redactions").$type<RedactionType[]>().default([]),

  // Report-level settings for multi-file cases
  reportDetailLevel: text("report_detail_level"), // 'concise' | 'standard' | 'detailed'
  includeTimestampsInNarrative: boolean("include_timestamps_in_narrative").default(false),
  hasAttachments: boolean("has_attachments").default(false),
  reportTemplateId: uuid("report_template_id").references(() => reportTemplates.id, { onDelete: "set null" }),
  callSign: text("call_sign"), // per-report override; null = use preference
  // Optional link to an investigation the officer picked as context when
  // creating the case: its brief/entities/glossary ride along to narrative
  // generation. Requires the investigations add-on and membership.
  investigationId: uuid("investigation_id").references(() => investigations.id, { onDelete: "set null" }),
  // Agency policy/statute sections retrieved for this case (draft, policy
  // check, Identify Statutes). Written before the Lambda invoke; merged by
  // sectionId. Drives the editor's Policies tab.
  policyReferences: jsonb("policy_references").$type<PolicyReference[]>().default([]),

  // "Add to Report" pending review state — populated server-side when an Add to
  // Report audio batch finishes processing and Bedrock returns its discrete
  // changes. The client polls this on case load to show the review banner.
  pendingSuggestionsChanges: jsonb("pending_suggestions_changes").$type<PendingSuggestionsChanges | null>(),

  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  discardedAt: timestamp("discarded_at", { withTimezone: true }),
  actions: integer("actions").notNull().default(0),
});

export type Case = typeof cases.$inferSelect;
export type NewCase = typeof cases.$inferInsert;

// Personal, user-owned categories for organizing cases (Slack-channel style).
export const caseCategories = pgTable("case_categories", {
  id: uuid("id").primaryKey().defaultRandom(),
  userId: uuid("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  position: integer("position").notNull().default(0), // ordering in the sidebar
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

export type CaseCategory = typeof caseCategories.$inferSelect;
export type NewCaseCategory = typeof caseCategories.$inferInsert;

// Many-to-many: a case can belong to multiple categories (tags).
export const caseCategoryAssignments = pgTable(
  "case_category_assignments",
  {
    caseId: uuid("case_id")
      .notNull()
      .references(() => cases.id, { onDelete: "cascade" }),
    categoryId: uuid("category_id")
      .notNull()
      .references(() => caseCategories.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    pk: primaryKey({ columns: [table.caseId, table.categoryId] }),
  })
);

export type CaseCategoryAssignment =
  typeof caseCategoryAssignments.$inferSelect;
export type NewCaseCategoryAssignment =
  typeof caseCategoryAssignments.$inferInsert;

// Per-user favorited cases — powers the special "Favorites" category.
export const caseFavorites = pgTable(
  "case_favorites",
  {
    userId: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    caseId: uuid("case_id")
      .notNull()
      .references(() => cases.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    pk: primaryKey({ columns: [table.userId, table.caseId] }),
  })
);

export type CaseFavorite = typeof caseFavorites.$inferSelect;
export type NewCaseFavorite = typeof caseFavorites.$inferInsert;

export const suggestions = pgTable("suggestions", {
  id: uuid("id").primaryKey().defaultRandom(),
  userId: uuid("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  caseId: uuid("case_id")
    .notNull()
    .references(() => cases.id, { onDelete: "cascade" }),
  content: text("content").notNull(),
  priority: integer("priority"),
  archived: boolean("archived").default(false),
  implemented: boolean("implemented").default(false),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

export type Suggestion = typeof suggestions.$inferSelect;
export type NewSuggestion = typeof suggestions.$inferInsert;

export const caseScores = pgTable("case_scores", {
  id: uuid("id").primaryKey().defaultRandom(),
  caseId: uuid("case_id")
    .notNull()
    .references(() => cases.id, { onDelete: "cascade" }),
  archived: boolean("archived").default(false),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  score: integer("score"),
});

export type CaseScore = typeof caseScores.$inferSelect;
export type NewCaseScore = typeof caseScores.$inferInsert;

export const AuditType = pgEnum("audit_type", [
  "first_draft",
  "enhanced_dictation",
  "pure_dictation",
  "enhanced_writing",
  "exported",
  "suggestions_insert",
  "suggestions_review",
  "redaction_upload",
  "redaction_edit",
  "redaction_export",
  "investigation_upload",
  "investigation_analysis",
  "investigation_lead_status",
  "investigation_export",
  "investigation_chat",
  "investigation_privilege",
  "investigation_deleted",
  "investigation_file_deleted",
  "investigation_timeline",
  "policy_chat",
]);

export const AuditTypeType = z.enum(AuditType.enumValues);

export const audits = pgTable("audits", {
  id: uuid("id").primaryKey().defaultRandom(),
  userId: uuid("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  // Nullable: audits anchor to a case OR a redaction document. Exactly one
  // of caseId / redactionDocumentId is expected to be set.
  // "set null" (not cascade) so export/usage audit history survives
  // agency-policy hard deletes of cases (data retention feature).
  caseId: uuid("case_id").references(() => cases.id, { onDelete: "set null" }),
  // "set null" (not cascade) so the audit trail of what was redacted
  // survives even if the document row is ever hard-deleted.
  redactionDocumentId: uuid("redaction_document_id").references(
    () => redactionDocuments.id,
    { onDelete: "set null" }
  ),
  // Investigations share the same trail. "set null" so the record of what
  // the AI flagged survives even if the investigation is hard-deleted —
  // that history is discoverable (Brady) and must outlive the row.
  investigationId: uuid("investigation_id").references(
    () => investigations.id,
    { onDelete: "set null" }
  ),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  content: text("content").notNull(),
  score: integer("score"),
  actions: integer("actions"),
  audit_type: AuditType("audit_type").default("exported"),
});

export type Audit = typeof audits.$inferSelect;
export type NewAudit = typeof audits.$inferInsert;

export const snippets = pgTable("snippets", {
  id: uuid("id").primaryKey().defaultRandom(),
  userId: uuid("user_id").references(() => users.id, { onDelete: "cascade" }),
  agencyId: uuid("agency_id").references(() => agencies.id),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  title: text("title").notNull(),
  content: text("content").notNull(),
  description: text("description"),
  priority: integer("priority"),
});

export type Snippet = typeof snippets.$inferSelect;
export type NewSnippet = typeof snippets.$inferInsert;

export const agencies = pgTable("agencies", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: text("name").notNull(),
  email: varchar("email", { length: 255 }).notNull().unique(),
  password: varchar("password", { length: 255 }),
  stripe_customer_id: varchar("stripe_customer_id", { length: 255 }),
  address: text("address"),
  city: varchar("city", { length: 255 }),
  state: varchar("state", { length: 255 }),
  zip: varchar("zip", { length: 20 }),
  country: varchar("country", { length: 2 }).default("US"),
  sworn_officers: integer("sworn_officers"),
  max_users: integer("max_users").default(0),
  // Evaluation seats granted via a signup agency evaluation code
  // (e.g. ?agencyEvaluationCode=US-ARMY-26). Kept separate from max_users
  // (purchased seats) so expiration is self-enforcing: these seats only
  // count while trial_expires_at is in the future — no cleanup job needed.
  trial_seats: integer("trial_seats").default(0),
  trial_expires_at: timestamp("trial_expires_at", { withTimezone: true }),
  trial_code: varchar("trial_code", { length: 64 }),
  reset_password_token: varchar("reset_password_token", { length: 255 }),
  reset_password_expires: timestamp("reset_password_expires"),
  mfa_enforced: boolean("mfa_enforced").default(false),
  mfa_allowed_methods: jsonb("mfa_allowed_methods")
    .$type<string[]>()
    .default(["totp", "email", "webauthn"]),
  mfa_trusted_device_days: integer("mfa_trusted_device_days").default(30),
  mfa_grace_period_days: integer("mfa_grace_period_days").default(0),
  // Data retention policy. When delete_on_export is on, officers see a
  // pre-checked "Delete case upon export" checkbox in the export dialogs.
  // Mode "trash" soft-deletes (recoverable until trash_retention_days
  // elapses); "permanent" hard-deletes the case and its S3 objects.
  delete_on_export: boolean("delete_on_export").default(false),
  delete_on_export_mode: text("delete_on_export_mode").default("trash"),
  // Opt-in auto-purge of trashed cases. When enabled, trashed cases older
  // than trash_retention_days are hard-deleted (DB + S3) by the hourly
  // purge cron. When disabled (default), nothing is ever purged — trash is
  // just hidden from the list after 30 days (legacy behavior).
  trash_retention_enabled: boolean("trash_retention_enabled").default(false),
  trash_retention_days: integer("trash_retention_days").default(30),
  // Timestamp and slug of the most-recently-generated findings page
  // (whether trial or annual). One report URL per agency — regenerating
  // overwrites whatever was there.
  findings_generated_at: timestamp("findings_generated_at", {
    withTimezone: true,
  }),
  findings_slug: text("findings_slug"),
  // Type of the most-recently-generated findings report. Drives the tab
  // label hint in the modal and the page header framing. "trial" for the
  // original short-window post-trial diagnostic, "annual" for the
  // retrospective department-wide review over a user-picked window.
  findings_type: text("findings_type"),
  // Post-trial survey round. Officers are prompted only when they have no
  // live post_trial_survey_responses row for this round, so extending a
  // trial never re-prompts; staff bump the round to ask everyone again.
  survey_round: integer("survey_round").notNull().default(1),
  survey_round_requested_at: timestamp("survey_round_requested_at", {
    withTimezone: true,
  }),
  // Staff "Send survey now": while this is in the future every officer in
  // the agency is prompted regardless of trial state (trigger "manual").
  survey_open_until: timestamp("survey_open_until", { withTimezone: true }),
  // Staff-portal archive: hides the agency from the main list (it moves to
  // the collapsed "Archived" list). Nothing else changes; not a delete.
  archived_at: timestamp("archived_at", { withTimezone: true }),
  // Staff-portal deal owner: one of SUPER_ADMIN_EMAILS (common/adminEmails.ts),
  // null when unassigned. Internal bookkeeping only.
  deal_owner: varchar("deal_owner", { length: 255 }),
  // Suggested starter questions for the officer policy chat, generated from
  // the agency's own documents (services/agencyDocuments/suggestions.ts) and
  // regenerated whenever the completed-document set changes (corpusKey).
  policy_chat_suggestions: jsonb("policy_chat_suggestions").$type<PolicyChatSuggestions>(),
  created_at: timestamp("created_at").defaultNow(),
  updated_at: timestamp("updated_at").defaultNow(),
  deleted_at: timestamp("deleted_at", { withTimezone: true }),
});

export type Agency = typeof agencies.$inferSelect;

// Topic of a suggested policy-chat question; the client picks an icon by it.
export const POLICY_SUGGESTION_TOPICS = ["force", "report", "vehicle", "equipment", "search", "conduct", "custody", "other"] as const;
export type PolicySuggestionTopic = (typeof POLICY_SUGGESTION_TOPICS)[number];
export type PolicyChatSuggestion = { text: string; topic: PolicySuggestionTopic };
export type PolicyChatSuggestions = {
  corpusKey: string;
  generatedAt: string;
  model?: string;
  questions: PolicyChatSuggestion[];
};
export type NewAgency = typeof agencies.$inferInsert;

export type DeleteOnExportMode = "trash" | "permanent";

// Async tracking for findings-page generation jobs. Annual reports can
// take minutes to run (large SQL aggregations + Claude call + S3 upload),
// so generation is kicked off in the background and the modal polls this
// table for progress. The row is the source of truth — the agency row's
// findings_type/slug/generated_at fields are only updated on success.
export const findings_jobs = pgTable("findings_jobs", {
  id: uuid("id").primaryKey().defaultRandom(),
  agency_id: uuid("agency_id")
    .notNull()
    .references(() => agencies.id),
  // "trial" or "annual" — drives the report's framing and (for annual)
  // requires window_start/window_end to scope SQL queries.
  type: text("type").notNull(),
  // "pending" → "running" → "done" | "failed". The worker transitions the
  // row through these states as it progresses.
  status: text("status").notNull().default("pending"),
  // Human-readable label for the current phase ("Looking up salary…",
  // "Snapshotting database…", etc.) — surfaced live in the modal.
  progress: text("progress"),
  // Per-job inputs the worker needs to reconstruct the bundle. Stored on
  // the row so the worker can restart if the Node process crashed mid-job
  // (rare, but the alternative is losing the request entirely).
  obscure_audio_split: boolean("obscure_audio_split").default(false),
  window_start: timestamp("window_start", { withTimezone: true }),
  window_end: timestamp("window_end", { withTimezone: true }),
  // When set, the report is scoped to only the top N officers by time saved
  // (saved_min DESC, then total_cases DESC) — all aggregations, the headline,
  // and the cost projection's per-officer pace are computed from just these
  // officers. NULL means "all officers" (the default, agency-wide report).
  top_users: integer("top_users"),
  // When set, only these officers' survey responses feed the report's
  // "What officers said" section (and the survey block of the findings
  // prompt). Every other number still covers the full officer scope. NULL
  // means every officer who answered.
  survey_user_ids: jsonb("survey_user_ids").$type<string[]>(),
  // Populated on success.
  result_slug: text("result_slug"),
  result_summary: jsonb("result_summary"),
  // Populated on failure (so the modal can surface a meaningful message).
  error: text("error"),
  // The admin who triggered the job (for the audit log).
  triggered_by: varchar("triggered_by", { length: 255 }),
  created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updated_at: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

export type FindingsJob = typeof findings_jobs.$inferSelect;
export type NewFindingsJob = typeof findings_jobs.$inferInsert;

// Post-trial officer survey responses. One live row per (user, round):
// "live" = superseded_at IS NULL. Staff re-ask a single officer by setting
// superseded_at (frees the slot for a fresh row in the same round) and
// re-ask the whole agency by bumping agencies.survey_round. Questions and
// validation live in common/postTrialSurvey.ts (shared with web + mobile);
// schema.ts must not import it, so the answers type stays loose here.
export const PostTrialSurveyStatusType = z.enum([
  "in_progress",
  "completed",
  "skipped",
]);
export const PostTrialSurveyTriggerType = z.enum(["final_week", "post_expiry"]);
export const PostTrialSurveyPlatformType = z.enum(["web", "mobile"]);

export const post_trial_survey_responses = pgTable(
  "post_trial_survey_responses",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    user_id: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    // Denormalized so staff and findings queries never join through users.
    agency_id: uuid("agency_id")
      .notNull()
      .references(() => agencies.id),
    // Copy of agencies.survey_round at creation.
    round: integer("round").notNull(),
    // SURVEY_VERSION at creation; lets aggregates skip incompatible rows later.
    version: integer("version").notNull(),
    // Keyed by question id; the quote lives in its own columns.
    answers: jsonb("answers")
      .$type<Record<string, number | string | string[]>>()
      .notNull()
      .default({}),
    quote: text("quote"),
    quote_consent: boolean("quote_consent").notNull().default(false),
    // in_progress → completed | skipped (third dismissal).
    status: text("status").notNull().default("in_progress"),
    // Which surface first created the row: final_week | post_expiry.
    trigger: text("trigger").notNull(),
    // Platform of the last write: web | mobile.
    platform: text("platform").notNull(),
    started_at: timestamp("started_at", { withTimezone: true }).defaultNow(),
    completed_at: timestamp("completed_at", { withTimezone: true }),
    dismissed_count: integer("dismissed_count").notNull().default(0),
    last_dismissed_at: timestamp("last_dismissed_at", { withTimezone: true }),
    superseded_at: timestamp("superseded_at", { withTimezone: true }),
    superseded_by: varchar("superseded_by", { length: 255 }),
    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
    updated_at: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    liveIdx: uniqueIndex("post_trial_survey_live_idx")
      .on(table.user_id, table.round)
      .where(sql`superseded_at IS NULL`),
    agencyRoundIdx: index("post_trial_survey_agency_round_idx").on(
      table.agency_id,
      table.round
    ),
  })
);

export type PostTrialSurveyResponse =
  typeof post_trial_survey_responses.$inferSelect;
export type NewPostTrialSurveyResponse =
  typeof post_trial_survey_responses.$inferInsert;

export const agency_invitations = pgTable("agency_invitations", {
  id: uuid("id").primaryKey().defaultRandom(),
  agency_id: uuid("agency_id")
    .notNull()
    .references(() => agencies.id),
  email: varchar("email", { length: 255 }).notNull(),
  token: varchar("token", { length: 255 }).notNull().unique(),
  role_id: integer("role_id")
    .references(() => roles.id)
    .default(1), // Default to officer role (assuming id 1)
  accepted: boolean("accepted").default(false),
  expires_at: timestamp("expires_at").notNull(),
  created_at: timestamp("created_at").defaultNow(),
  updated_at: timestamp("updated_at").defaultNow(),
  deleted_at: timestamp("deleted_at", { withTimezone: true }),
});

export type AgencyInvitation = typeof agency_invitations.$inferSelect;
export type NewAgencyInvitation = typeof agency_invitations.$inferInsert;

export const agency_transfer_invitations = pgTable("agency_transfer_invitations", {
  id: uuid("id").primaryKey().defaultRandom(),
  agency_id: uuid("agency_id")
    .notNull()
    .references(() => agencies.id),
  current_owner_email: varchar("current_owner_email", { length: 255 }).notNull(),
  new_owner_email: varchar("new_owner_email", { length: 255 }).notNull(),
  token: varchar("token", { length: 255 }).notNull().unique(),
  accepted: boolean("accepted").default(false),
  expires_at: timestamp("expires_at").notNull(),
  created_at: timestamp("created_at").defaultNow(),
  updated_at: timestamp("updated_at").defaultNow(),
  deleted_at: timestamp("deleted_at", { withTimezone: true }),
});

export type AgencyTransferInvitation = typeof agency_transfer_invitations.$inferSelect;
export type NewAgencyTransferInvitation = typeof agency_transfer_invitations.$inferInsert;

export const user_email_change_invitations = pgTable("user_email_change_invitations", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id")
    .notNull()
    .references(() => users.id),
  current_email: varchar("current_email", { length: 255 }).notNull(),
  new_email: varchar("new_email", { length: 255 }).notNull(),
  token: varchar("token", { length: 255 }).notNull().unique(),
  accepted: boolean("accepted").default(false),
  expires_at: timestamp("expires_at").notNull(),
  created_at: timestamp("created_at").defaultNow(),
  updated_at: timestamp("updated_at").defaultNow(),
  deleted_at: timestamp("deleted_at", { withTimezone: true }),
});

export type UserEmailChangeInvitation = typeof user_email_change_invitations.$inferSelect;
export type NewUserEmailChangeInvitation = typeof user_email_change_invitations.$inferInsert;

export const agency_admins = pgTable("agency_admins", {
  id: uuid("id").primaryKey().defaultRandom(),
  agency_id: uuid("agency_id")
    .notNull()
    .references(() => agencies.id),
  name: text("name"),
  email: varchar("email", { length: 255 }).notNull().unique(),
  password: varchar("password", { length: 255 }).notNull(),
  reset_password_token: varchar("reset_password_token", { length: 255 }),
  reset_password_expires: timestamp("reset_password_expires"),
  mfa_setup_required_at: timestamp("mfa_setup_required_at"),
  seen_tutorials: jsonb("seen_tutorials").$type<string[]>().default([]),
  created_at: timestamp("created_at").defaultNow(),
  updated_at: timestamp("updated_at").defaultNow(),
  deleted_at: timestamp("deleted_at", { withTimezone: true }),
});

export type AgencyAdmin = typeof agency_admins.$inferSelect;
export type NewAgencyAdmin = typeof agency_admins.$inferInsert;

export const agency_admin_invitations = pgTable("agency_admin_invitations", {
  id: uuid("id").primaryKey().defaultRandom(),
  agency_id: uuid("agency_id")
    .notNull()
    .references(() => agencies.id),
  invited_by: uuid("invited_by")
    .notNull()
    .references(() => agency_admins.id),
  email: varchar("email", { length: 255 }).notNull(),
  token: varchar("token", { length: 255 }).notNull().unique(),
  accepted: boolean("accepted").default(false),
  expires_at: timestamp("expires_at").notNull(),
  created_at: timestamp("created_at").defaultNow(),
  updated_at: timestamp("updated_at").defaultNow(),
  deleted_at: timestamp("deleted_at", { withTimezone: true }),
});

export type AgencyAdminInvitation = typeof agency_admin_invitations.$inferSelect;
export type NewAgencyAdminInvitation = typeof agency_admin_invitations.$inferInsert;

// Staff-sent trial invitations: email -> Trial Requirements page -> agency
// signup with this token, which grants the seats and expiry automatically.
export const agency_trial_invitations = pgTable("agency_trial_invitations", {
  id: uuid("id").primaryKey().defaultRandom(),
  email: varchar("email", { length: 255 }).notNull(),
  token: varchar("token", { length: 255 }).notNull().unique(),
  officer_seats: integer("officer_seats").notNull().default(0),
  investigator_seats: integer("investigator_seats").notNull().default(0),
  trial_days: integer("trial_days").notNull().default(45),
  // Staff have no agency_admins row, so the sender is stored by email.
  invited_by_email: varchar("invited_by_email", { length: 255 }).notNull(),
  expires_at: timestamp("expires_at").notNull(),
  agreed_at: timestamp("agreed_at", { withTimezone: true }),
  accepted_at: timestamp("accepted_at", { withTimezone: true }),
  agency_id: uuid("agency_id").references(() => agencies.id),
  created_at: timestamp("created_at").defaultNow(),
  updated_at: timestamp("updated_at").defaultNow(),
  deleted_at: timestamp("deleted_at", { withTimezone: true }),
});

export type AgencyTrialInvitation = typeof agency_trial_invitations.$inferSelect;
export type NewAgencyTrialInvitation = typeof agency_trial_invitations.$inferInsert;

export const roles = pgTable("roles", {
  id: serial("id").primaryKey(),
  name: text("name").notNull().unique(),
});

export type Role = typeof roles.$inferSelect;
export type NewRole = typeof roles.$inferInsert;

export const user_roles = pgTable(
  "user_roles",
  {
    user_id: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    role_id: integer("role_id")
      .notNull()
      .references(() => roles.id),
  },
  (table) => ({
    pk: primaryKey({ columns: [table.user_id, table.role_id] }),
  })
);

export type UserRole = typeof user_roles.$inferSelect;
export type NewUserRole = typeof user_roles.$inferInsert;

export type AttributeMapping = {
  email?: string;
  name?: string;
  badgeID?: string;
  roles?: string;
};

export const sso_configurations = pgTable("sso_configurations", {
  id: uuid("id").primaryKey().defaultRandom(),
  agency_id: uuid("agency_id")
    .notNull()
    .references(() => agencies.id),
  provider_type: ExternalAuthProvider("provider_type").notNull(),
  provider_name: text("provider_name").notNull(),
  domain: text("domain").notNull(),

  // SAML-specific fields
  saml_sso_url: text("saml_sso_url"),
  saml_issuer: text("saml_issuer"),
  saml_certificate: text("saml_certificate"),

  // OIDC-specific fields
  oidc_client_id: text("oidc_client_id"),
  oidc_client_secret: text("oidc_client_secret"),
  oidc_discovery_url: text("oidc_discovery_url"),

  // Common fields
  attribute_mapping: jsonb("attribute_mapping")
    .$type<AttributeMapping>()
    .default({}),
  auto_provision: boolean("auto_provision").default(true),
  default_role_id: integer("default_role_id").references(() => roles.id),
  enabled: boolean("enabled").default(true),

  created_at: timestamp("created_at").defaultNow(),
  updated_at: timestamp("updated_at").defaultNow(),
});

export type SSOConfiguration = typeof sso_configurations.$inferSelect;
export type NewSSOConfiguration = typeof sso_configurations.$inferInsert;

export const sso_sessions = pgTable("sso_sessions", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  sso_config_id: uuid("sso_config_id")
    .notNull()
    .references(() => sso_configurations.id),
  external_session_id: text("external_session_id"),
  saml_session_index: text("saml_session_index"),
  expires_at: timestamp("expires_at"),
  created_at: timestamp("created_at").defaultNow(),
});

export type SSOSession = typeof sso_sessions.$inferSelect;
export type NewSSOSession = typeof sso_sessions.$inferInsert;

export const evidence_integrations = pgTable(
  "evidence_integrations",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    agency_id: uuid("agency_id")
      .notNull()
      .references(() => agencies.id),
    vendor: EvidenceVendor("vendor").notNull(),
    enabled: boolean("enabled").default(false),
    credentials: text("credentials").notNull(),
    access_token: text("access_token"),
    token_expires_at: timestamp("token_expires_at"),
    last_verified_at: timestamp("last_verified_at"),
    created_at: timestamp("created_at").defaultNow(),
    updated_at: timestamp("updated_at").defaultNow(),
  },
  (table) => ({
    agencyVendorUnique: unique("evidence_integrations_agency_vendor").on(table.agency_id, table.vendor),
  }),
);

export type EvidenceIntegration = typeof evidence_integrations.$inferSelect;
export type NewEvidenceIntegration = typeof evidence_integrations.$inferInsert;

export const checklistTemplates = pgTable("checklist_templates", {
  id: uuid("id").primaryKey().defaultRandom(),
  agency_id: uuid("agency_id").references(() => agencies.id),
  name: text("name").notNull(), // "Initial Investigation Checklist"
  description: text("description"),
  items: jsonb("items").$type<ChecklistItem[]>().notNull(),
  is_active: boolean("is_active").default(true),
  created_at: timestamp("created_at").defaultNow(),
  updated_at: timestamp("updated_at").defaultNow(),
});

export type ChecklistTemplate = typeof checklistTemplates.$inferSelect;
export type NewChecklistTemplate = typeof checklistTemplates.$inferInsert;

export const case_checklists = pgTable("case_checklists", {
  id: uuid("id").primaryKey().defaultRandom(),
  case_id: uuid("case_id").references(() => cases.id, { onDelete: "cascade" }),
  template_id: uuid("template_id").references(() => checklistTemplates.id),
  user_id: uuid("user_id").references(() => users.id, { onDelete: "cascade" }),
  checklist_data: jsonb("checklist_data").$type<ChecklistData>(),
  completed_at: timestamp("completed_at"),
  created_at: timestamp("created_at").defaultNow(),
  updated_at: timestamp("updated_at").defaultNow(),
});

export type CaseChecklist = typeof case_checklists.$inferSelect;
export type NewCaseChecklist = typeof case_checklists.$inferInsert;

// Report Templates - reusable templates for shaping AI-generated reports
export const reportTemplates = pgTable("report_templates", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").references(() => users.id, { onDelete: "cascade" }),
  agency_id: uuid("agency_id").references(() => agencies.id),
  name: text("name").notNull(),
  description: text("description"),
  content: jsonb("content").$type<ReportTemplateContent>().notNull().default({
    header: "",
    footer: "",
    body: "",
  }),
  is_default: boolean("is_default").default(false),
  created_at: timestamp("created_at").defaultNow(),
  updated_at: timestamp("updated_at").defaultNow(),
});

export type ReportTemplate = typeof reportTemplates.$inferSelect;
export type NewReportTemplate = typeof reportTemplates.$inferInsert;

export const case_files = pgTable("case_files", {
  id: uuid("id").primaryKey().defaultRandom(),
  case_id: uuid("case_id")
    .notNull()
    .references(() => cases.id, { onDelete: "cascade" }),

  // Common file information
  file_url: text("file_url").notNull(),
  file_name: text("file_name"),
  file_size: text("file_size"), // Store as text for human-readable format
  file_type: text("file_type"), // MIME type: 'audio/mp4', 'image/jpeg', etc.
  media_type: FileMediaType("media_type").notNull(), // High-level categorization

  // Processing information
  processing_status: FileProcessingStatus("processing_status").default("pending"),
  error_message: text("error_message"),

  // Type-specific metadata (use appropriate type based on media_type)
  metadata: jsonb("metadata").$type<
    | AudioFileMetadata
    | ImageFileMetadata
    | VideoFileMetadata
    | DocumentFileMetadata
  >(),

  // Display order and categorization
  order: integer("order").default(0),
  category: text("category"), // Optional: 'evidence', 'scene-photo', 'witness-statement', etc.

  created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updated_at: timestamp("updated_at", { withTimezone: true }).defaultNow(),
},
// Reused uploads share a file_url across rows; deletes look it up to see
// whether anything else still points at the object.
(table) => ({
  fileUrlIdx: index("case_files_file_url_idx").on(table.file_url),
}));

export type CaseFile = typeof case_files.$inferSelect;
export type NewCaseFile = typeof case_files.$inferInsert;

// The Files library: files an officer saved straight to Files (uploaded or
// recorded) with no report attached. Rows that belong to a report stay in
// case_files / form_submission_files; the Files list is a UNION over all three.
// A library file owns its S3 object and its processing results (transcript,
// extracted text), so a report created from it can reuse them without copying.
export const library_files = pgTable(
  "library_files",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    user_id: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),

    file_url: text("file_url").notNull().default(""), // empty until uploaded
    file_name: text("file_name"),
    file_size: text("file_size"),
    file_type: text("file_type"), // MIME type
    // Video stays "audio" (the transcript pipeline relies on it), like case_files.
    media_type: FileMediaType("media_type").notNull(),
    // What the Files page shows and filters on.
    kind: text("kind").$type<"audio" | "video" | "image" | "document" | "spreadsheet">().notNull(),

    processing_status: FileProcessingStatus("processing_status").default("pending"),
    error_message: text("error_message"),

    metadata: jsonb("metadata").$type<
      | AudioFileMetadata
      | ImageFileMetadata
      | VideoFileMetadata
      | DocumentFileMetadata
    >(),

    source: text("source").$type<"upload" | "recording">().notNull().default("upload"),

    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
    updated_at: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    userCreatedIdx: index("library_files_user_created_idx").on(table.user_id, table.created_at),
    fileUrlIdx: index("library_files_file_url_idx").on(table.file_url),
  }),
);

export type LibraryFile = typeof library_files.$inferSelect;
export type NewLibraryFile = typeof library_files.$inferInsert;

// ---- Case footage analysis ---------------------------------------------------

// The visual passes over a case's body cam / surveillance upload. Mirrors
// investigation_jobs (claim with SKIP LOCKED, retries, per-stage timeouts)
// but for case_files rows, which have no investigation.
export const VideoJobStage = pgEnum("video_job_stage", [
  "triage",
  "detail",
  "zoom",
  "check",
]);
export const VideoJobStageType = z.enum(VideoJobStage.enumValues);
// Same lifecycle as investigation_job_status; declared here because that
// enum is defined further down and pgTable evaluates eagerly.
export const VideoJobStatus = pgEnum("video_job_status", [
  "queued",
  "dispatched",
  "running",
  "complete",
  "failed",
]);

export const video_analysis_jobs = pgTable(
  "video_analysis_jobs",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    case_id: uuid("case_id")
      .notNull()
      .references(() => cases.id, { onDelete: "cascade" }),
    case_file_id: uuid("case_file_id")
      .notNull()
      .references(() => case_files.id, { onDelete: "cascade" }),
    stage: VideoJobStage("stage").notNull(),
    status: VideoJobStatus("status").notNull().default("queued"),
    attempts: integer("attempts").default(0),
    max_attempts: integer("max_attempts").default(3),
    priority: integer("priority").default(10),
    payload: jsonb("payload").$type<Record<string, any>>(),
    dispatched_at: timestamp("dispatched_at", { withTimezone: true }),
    started_at: timestamp("started_at", { withTimezone: true }),
    finished_at: timestamp("finished_at", { withTimezone: true }),
    next_attempt_at: timestamp("next_attempt_at", { withTimezone: true }),
    error_message: text("error_message"),
    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
    updated_at: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    fileStageUnique: unique("video_analysis_jobs_file_stage_unique").on(
      table.case_file_id,
      table.stage
    ),
    queueIdx: index("video_analysis_jobs_queue_idx").on(
      table.status,
      table.stage,
      table.priority
    ),
  })
);
export type VideoAnalysisJob = typeof video_analysis_jobs.$inferSelect;

// Text extraction for non-audio files (PDF OCR, images, Word, spreadsheets):
// one job per file, for either a report's file or a library file. Same queue
// semantics as video_analysis_jobs; services/files/extractDispatcher.ts runs it.
export const file_extract_jobs = pgTable(
  "file_extract_jobs",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    // Exactly one of these is set (enforced in code).
    case_file_id: uuid("case_file_id").references(() => case_files.id, { onDelete: "cascade" }),
    library_file_id: uuid("library_file_id").references(() => library_files.id, { onDelete: "cascade" }),
    kind: text("kind").notNull(), // pdf | word | text | image | spreadsheet
    status: VideoJobStatus("status").notNull().default("queued"),
    attempts: integer("attempts").default(0),
    max_attempts: integer("max_attempts").default(3),
    next_attempt_at: timestamp("next_attempt_at", { withTimezone: true }),
    started_at: timestamp("started_at", { withTimezone: true }),
    finished_at: timestamp("finished_at", { withTimezone: true }),
    error_message: text("error_message"),
    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
    updated_at: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    caseFileUnique: unique("file_extract_jobs_case_file_unique").on(table.case_file_id),
    libraryFileUnique: unique("file_extract_jobs_library_file_unique").on(table.library_file_id),
    queueIdx: index("file_extract_jobs_queue_idx").on(table.status, table.next_attempt_at),
  }),
);
export type FileExtractJob = typeof file_extract_jobs.$inferSelect;

// The event log, one row per visual event, so the footage panel, the
// export, and the footage check can query by time without reading S3.
export const case_video_events = pgTable(
  "case_video_events",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    case_id: uuid("case_id")
      .notNull()
      .references(() => cases.id, { onDelete: "cascade" }),
    case_file_id: uuid("case_file_id")
      .notNull()
      .references(() => case_files.id, { onDelete: "cascade" }),
    event_id: text("event_id").notNull(), // VideoEvent.id
    t_start_ms: integer("t_start_ms").notNull(),
    t_end_ms: integer("t_end_ms").notNull(),
    kind: text("kind").notNull(),
    description: text("description").notNull(),
    confidence: decimal("confidence", { precision: 3, scale: 2 }),
    event: jsonb("event").$type<VideoEvent>().notNull(),
    analysis_version: integer("analysis_version").default(0),
    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    fileEventUnique: unique("case_video_events_file_event_unique").on(
      table.case_file_id,
      table.event_id
    ),
    fileTimeIdx: index("case_video_events_file_time_idx").on(
      table.case_file_id,
      table.t_start_ms
    ),
  })
);
export type CaseVideoEvent = typeof case_video_events.$inferSelect;

// ============================================================================
// AGENCY DOCUMENTS (policy manuals, statutes, ordinances uploaded by admins)
// ============================================================================
// Admins upload a department's own rules; the server extracts, splits by the
// document's own section headings, auto-tags every section, embeds, and
// retrieves the relevant sections at report-generation time so narratives
// cite agency sections (documents first, state statutes as fallback) and a
// post-generation policy check posts suggestions. Officers browse and chat
// over the same corpus under /policies. See services/agencyDocuments/.

// tsvector isn't a first-class drizzle column type. Declaring it here (rather
// than in a hand-written migration) matters: drizzle-kit diffs the live DB
// against this file on every `generate`, and any column the schema doesn't
// know about is emitted as DROP COLUMN in the next migration.
const tsvector = customType<{ data: string }>({
  dataType() {
    return "tsvector";
  },
});

export const AgencyDocumentType = pgEnum("agency_document_type", [
  "statute",
  "policy",
  "other",
]);
export const AgencyDocumentTypeType = z.enum(AgencyDocumentType.enumValues);

export const AgencyDocumentStatus = pgEnum("agency_document_status", [
  "pending",
  "processing",
  "completed",
  "failed",
]);
export const AgencyDocumentStatusType = z.enum(AgencyDocumentStatus.enumValues);

// extract -> section -> tag -> embed -> done. Persisted after every step so a
// retry resumes where it failed instead of paying for Textract again.
export const AgencyDocumentStage = pgEnum("agency_document_stage", [
  "extract",
  "section",
  "tag",
  "embed",
  "done",
]);
export const AgencyDocumentStageType = z.enum(AgencyDocumentStage.enumValues);

export const AgencyDocumentTagOrigin = pgEnum("agency_document_tag_origin", [
  "auto",
  "admin",
]);

// auto = the tagger proposed it; admin = an admin added it; admin_removed =
// the tagger proposed it and an admin removed it (kept so a reprocess never
// re-adds it).
export const AgencyDocumentSectionTagSource = pgEnum(
  "agency_document_section_tag_source",
  ["auto", "admin", "admin_removed"]
);

// Normalized 0-1 page box, like DocLeadAnchor's boxes but with the page on
// each box because a section can run across pages.
export type SectionAnchorBox = { page: number; x: number; y: number; w: number; h: number };
export type SectionAnchor = { boxes: SectionAnchorBox[] };

export type AgencyDocumentMetadata = {
  extract?: {
    version: number;
    kind: string;
    chars: number;
    pageCount?: number;
    extractedAt: string;
    // Word/txt documents are paginated into ~3,200-char pseudo-pages; viewers
    // must not treat those as rendered PDF pages.
    textPages?: boolean;
  };
  tagging?: {
    model: string;
    batches: number;
    newTags: number;
    tokensIn: number;
    tokensOut: number;
  };
  // Admin tag edits that could not be re-attached to a section after a
  // reprocess changed the section split.
  droppedOverrides?: number;
  // Tags the admin pinned at upload; applied as admin tags to every section.
  presetTagIds?: string[];
};

// The row is also the ingest job (status/stage/attempts), claimed by
// services/agencyDocuments/dispatcher.ts with FOR UPDATE SKIP LOCKED.
export const agency_documents = pgTable(
  "agency_documents",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    agency_id: uuid("agency_id")
      .notNull()
      .references(() => agencies.id, { onDelete: "cascade" }),
    title: text("title").notNull(),
    file_name: text("file_name"),
    s3_key: text("s3_key").notNull(),
    file_size: integer("file_size"), // bytes
    doc_type: AgencyDocumentType("doc_type").notNull().default("policy"),
    status: AgencyDocumentStatus("status").notNull().default("pending"),
    stage: AgencyDocumentStage("stage").notNull().default("extract"),
    attempts: integer("attempts").default(0),
    max_attempts: integer("max_attempts").default(3),
    next_attempt_at: timestamp("next_attempt_at", { withTimezone: true }),
    started_at: timestamp("started_at", { withTimezone: true }),
    finished_at: timestamp("finished_at", { withTimezone: true }),
    page_count: integer("page_count"),
    section_count: integer("section_count"),
    chunk_count: integer("chunk_count"),
    error_message: text("error_message"),
    embedding_model: text("embedding_model"),
    metadata: jsonb("metadata").$type<AgencyDocumentMetadata>(),
    // The admin session may be the legacy Agency principal with no admin row,
    // so the uploader is recorded by email rather than by FK.
    created_by_email: varchar("created_by_email", { length: 255 }),
    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
    updated_at: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    agencyIdx: index("agency_documents_agency_idx").on(table.agency_id),
    queueIdx: index("agency_documents_queue_idx").on(
      table.status,
      table.next_attempt_at
    ),
  })
);
export type AgencyDocument = typeof agency_documents.$inferSelect;
export type NewAgencyDocument = typeof agency_documents.$inferInsert;

// The stable unit tags attach to and the admin edits. Chunks (below) are the
// embedded pieces within a section.
export const agency_document_sections = pgTable(
  "agency_document_sections",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    document_id: uuid("document_id")
      .notNull()
      .references(() => agency_documents.id, { onDelete: "cascade" }),
    agency_id: uuid("agency_id")
      .notNull()
      .references(() => agencies.id, { onDelete: "cascade" }),
    section_index: integer("section_index").notNull(),
    // Normalized "<number>|<heading>", used to re-attach admin tag edits after
    // a reprocess re-splits the document.
    section_key: text("section_key").notNull(),
    section_number: text("section_number"), // "300.3.1", "9.12.040", "ARTICLE IV"
    heading: text("heading"),
    section_path: text("section_path"), // "300 > 300.3 > 300.3.1"
    page_start: integer("page_start"),
    page_end: integer("page_end"),
    char_count: integer("char_count").notNull(),
    content: text("content").notNull(),
    // One normalized box per page the section spans (from Textract line
    // geometry, services/agencyDocuments/anchors.ts); null for Word/txt
    // documents, which have no page geometry.
    anchor: jsonb("anchor").$type<SectionAnchor | null>(),
    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    docIndexUnique: unique("agency_document_sections_doc_index_unique").on(
      table.document_id,
      table.section_index
    ),
    agencyIdx: index("agency_document_sections_agency_idx").on(table.agency_id),
  })
);
export type AgencyDocumentSection = typeof agency_document_sections.$inferSelect;

// Embedded units. No HNSW index on purpose (same reasoning as
// investigation_chunks): a per-agency corpus is a few thousand rows and an
// exact scan under `WHERE agency_id = ?` beats a post-filtered global index.
export const agency_document_chunks = pgTable(
  "agency_document_chunks",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    document_id: uuid("document_id")
      .notNull()
      .references(() => agency_documents.id, { onDelete: "cascade" }),
    section_id: uuid("section_id")
      .notNull()
      .references(() => agency_document_sections.id, { onDelete: "cascade" }),
    agency_id: uuid("agency_id")
      .notNull()
      .references(() => agencies.id, { onDelete: "cascade" }),
    chunk_index: integer("chunk_index").notNull(),
    content: text("content").notNull(),
    embedding: vector("embedding", { dimensions: 1024 }),
    tsv: tsvector("tsv").generatedAlwaysAs(
      sql`to_tsvector('simple', coalesce(content, ''))`
    ),
    page_start: integer("page_start"),
    page_end: integer("page_end"),
    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    agencyIdx: index("agency_document_chunks_agency_idx").on(table.agency_id),
    documentIdx: index("agency_document_chunks_document_idx").on(
      table.document_id
    ),
    sectionIdx: index("agency_document_chunks_section_idx").on(table.section_id),
    tsvIdx: index("agency_document_chunks_tsv_idx").using("gin", table.tsv),
  })
);
export type AgencyDocumentChunk = typeof agency_document_chunks.$inferSelect;

// Agency-level tag vocabulary. Seeded with defaults on first use; the tagger
// may propose new ones (origin "auto"); admins rename/merge/delete.
export const agency_document_tags = pgTable(
  "agency_document_tags",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    agency_id: uuid("agency_id")
      .notNull()
      .references(() => agencies.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    slug: text("slug").notNull(),
    description: text("description"), // "applies when ..."
    color: text("color"),
    origin: AgencyDocumentTagOrigin("origin").notNull().default("auto"),
    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
    updated_at: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    agencySlugUnique: unique("agency_document_tags_agency_slug_unique").on(
      table.agency_id,
      table.slug
    ),
  })
);
export type AgencyDocumentTag = typeof agency_document_tags.$inferSelect;

export const agency_document_section_tags = pgTable(
  "agency_document_section_tags",
  {
    section_id: uuid("section_id")
      .notNull()
      .references(() => agency_document_sections.id, { onDelete: "cascade" }),
    tag_id: uuid("tag_id")
      .notNull()
      .references(() => agency_document_tags.id, { onDelete: "cascade" }),
    source: AgencyDocumentSectionTagSource("source").notNull().default("auto"),
    confidence: real("confidence"),
    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    pk: primaryKey({ columns: [table.section_id, table.tag_id] }),
    tagIdx: index("agency_document_section_tags_tag_idx").on(table.tag_id),
  })
);
export type AgencyDocumentSectionTag =
  typeof agency_document_section_tags.$inferSelect;

// Officer chat over the agency's documents (/policies). Same shape as the
// investigation chat tables; citations number globally per thread.
export const policy_chat_threads = pgTable(
  "policy_chat_threads",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    agency_id: uuid("agency_id")
      .notNull()
      .references(() => agencies.id, { onDelete: "cascade" }),
    user_id: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    title: text("title"),
    citation_counter: integer("citation_counter").default(0),
    last_message_at: timestamp("last_message_at", { withTimezone: true }),
    archived_at: timestamp("archived_at", { withTimezone: true }),
    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    agencyUserIdx: index("policy_chat_threads_agency_user_idx").on(
      table.agency_id,
      table.user_id
    ),
  })
);
export type PolicyChatThread = typeof policy_chat_threads.$inferSelect;

export const policy_chat_messages = pgTable(
  "policy_chat_messages",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    thread_id: uuid("thread_id")
      .notNull()
      .references(() => policy_chat_threads.id, { onDelete: "cascade" }),
    role: text("role").notNull(), // "user" | "assistant"
    content: text("content").notNull(),
    blocks: jsonb("blocks").$type<any[]>(),
    citations: jsonb("citations").$type<ChatCitation[]>(),
    token_usage: jsonb("token_usage").$type<ChatTokenUsage>(),
    model: text("model"),
    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    threadIdx: index("policy_chat_messages_thread_idx").on(
      table.thread_id,
      table.created_at
    ),
  })
);
export type PolicyChatMessage = typeof policy_chat_messages.$inferSelect;

// Agency Management Action enum for audit logging
export const AgencyManagementAction = pgEnum("agency_management_action", [
  "update_max_users",
  "update_subscription_status",
  "update_subscription_end_date",
  "create_subscription",
  "send_transfer_invitation",
  "resend_transfer_invitation",
  "cancel_transfer_invitation",
  "remove_user",
  "invite_user",
  "cancel_invitation",
  "resend_invitation",
  "invite_admin",
  "resend_admin_invitation",
  "cancel_admin_invitation",
  "remove_admin",
  "send_user_email_change",
  "grant_addon_seat",
  "revoke_addon_seat",
  "set_addon_seats",
  "clear_abuse_flag",
  "set_abuse_flag",
  "set_addon_allowance",
  "grant_addon_hours",
  "delete_agency",
  "feature_request_status",
  "feature_request_merge",
  "feature_request_hide",
  "feature_request_edit",
  "survey_request_again",
  "survey_request_again_officer",
  "survey_open",
  "survey_close",
  "survey_open_officer",
  "survey_close_officer",
  "archive_agency",
  "unarchive_agency",
  "survey_delete_response",
  "set_deal_owner",
  "upload_agency_document",
  "delete_agency_document",
  "reprocess_agency_document",
  "update_document_tags",
  "invite_agency_trial",
  "resend_agency_trial_invite",
  "cancel_agency_trial_invite",
]);

export const AgencyManagementActionType = z.enum(
  AgencyManagementAction.enumValues
);

export type AgencyManagementMetadata = {
  agency_id?: string;
  agency_name?: string;
  old_value?: any;
  new_value?: any;
  changes?: Record<string, { old: any; new: any }>;
  error?: string;
  request_body?: any;
  new_owner_email?: string;
  current_owner_email?: string;
  invitation_id?: string;
  user_id?: string;
  new_email?: string;
  current_email?: string;
  feature_request_id?: string;
  feature_request_title?: string;
  target_id?: string;
  response_id?: string;
  round?: number;
  user_ids?: string[];
  document_id?: string;
  document_title?: string;
  section_id?: string;
  tag_id?: string;
  tag_ids?: string[];
};

export const agency_management_audits = pgTable("agency_management_audits", {
  id: uuid("id").primaryKey().defaultRandom(),
  admin_email: varchar("admin_email", { length: 255 }).notNull(),
  action: AgencyManagementAction("action").notNull(),
  agency_id: uuid("agency_id").references(() => agencies.id),
  metadata: jsonb("metadata").$type<AgencyManagementMetadata>(),
  ip_address: varchar("ip_address", { length: 45 }), // Supports both IPv4 and IPv6
  user_agent: text("user_agent"),
  success: boolean("success").default(true),
  created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
});

export type AgencyManagementAudit = typeof agency_management_audits.$inferSelect;
export type NewAgencyManagementAudit =
  typeof agency_management_audits.$inferInsert;

// ============================================================================
// Security Audit Log (CJIS auditable events)
// ============================================================================

export const SecurityEventType = pgEnum("security_event_type", [
  "login_success",
  "login_failed",
  "logout",
  "mfa_challenge_success",
  "mfa_challenge_failed",
  "mfa_enrolled",
  "mfa_disabled",
  "password_changed",
  "concurrent_session_detected",
  "access_denied",
  "session_expired",
  "role_changed",
  "account_created",
  "account_deleted",
  "privilege_modified",
]);

export const SecurityEventTypeType = z.enum(SecurityEventType.enumValues);

export type SecurityActorType = "user" | "agency_admin" | "agency" | "anonymous";

export type SecurityAuditMetadata = {
  reason?: string;
  path?: string;
  method?: string;
  mfa_method?: string;
  active_session_count?: number;
  prior_ip?: string;
  prior_user_agent?: string;
  target_user_id?: string;
  target_email?: string;
  old_value?: any;
  new_value?: any;
  error?: string;
  [key: string]: any;
};

export const security_audit_log = pgTable("security_audit_log", {
  id: uuid("id").primaryKey().defaultRandom(),
  event_type: SecurityEventType("event_type").notNull(),
  success: boolean("success").notNull().default(true),
  user_id: uuid("user_id"),
  user_email: varchar("user_email", { length: 255 }),
  actor_type: varchar("actor_type", { length: 32 }),
  agency_id: uuid("agency_id").references(() => agencies.id),
  session_id: varchar("session_id", { length: 255 }),
  ip_address: varchar("ip_address", { length: 45 }),
  user_agent: text("user_agent"),
  metadata: jsonb("metadata").$type<SecurityAuditMetadata>(),
  created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
});

export type SecurityAuditLog = typeof security_audit_log.$inferSelect;
export type NewSecurityAuditLog = typeof security_audit_log.$inferInsert;

// ============================================================================
// Form Templates & Submissions
// ============================================================================

// Form field type enum
export const FormFieldType = pgEnum("form_field_type", [
  "text",
  "textarea",
  "checkbox",
  "radio",
  "dropdown",
  "date",
  "time",
  "signature",
  "number",
]);

// "circle" (a printed list of words the filler circles) lives only in the JSONB
// field definitions, never in a column, so it is added here and the unused pg
// enum above is left alone (no migration for it).
export const FormFieldTypeType = z.enum([...FormFieldType.enumValues, "circle"]);

// Form template scope - personal or agency-wide
export const FormTemplateScope = pgEnum("form_template_scope", [
  "personal",
  "agency",
]);

export const FormTemplateScopeType = z.enum(FormTemplateScope.enumValues);

// Form template output type
export const FormOutputType = pgEnum("form_output_type", [
  "pdf",
  "text",
  "text_ai",
]);

export const FormOutputTypeType = z.enum(FormOutputType.enumValues);

// Form submission status
export const FormSubmissionStatus = pgEnum("form_submission_status", [
  "draft",
  "processing",
  "completed",
  "error",
]);

export const FormSubmissionStatusType = z.enum(FormSubmissionStatus.enumValues);

// Form section type - describes a section grouping in a form template
export type FormSection = {
  name: string;
  context?: string;  // AI guidance for what information to extract for fields in this section
};

// Form field definition type - describes a single field in a form template
export type FormFieldDefinition = {
  id: string;
  name: string;           // Field name from PDF (e.g., "Text1" or "officer_name")
  label: string;          // Human-readable label (e.g., "Officer Name")
  type: z.infer<typeof FormFieldTypeType>;
  required: boolean;
  page: number;           // PDF page number (1-indexed)
  rect: {                 // Position on PDF (coordinates)
    x: number;
    y: number;
    width: number;
    height: number;
  };
  options?: string[];     // For dropdown/radio fields, and the printed words of a circle field
  // circle: one rect per option (same index as `options`), same coordinates as
  // `rect`, which is their bounding box. The filler draws an ellipse around the
  // chosen option(s).
  optionRects?: { x: number; y: number; width: number; height: number }[];
  // circle: the form lets several options be circled ("circle all that apply").
  // Off means circle one. The value is a string, or string[] when multiple.
  multiple?: boolean;
  defaultValue?: string;
  placeholder?: string;
  order: number;          // Display order
  section?: string;       // Form section/question (e.g., "Was force used on a suspect in mechanical restraints?")
  additionalContext?: string;  // Additional context for AI extraction (e.g., "Full name of the reporting officer")
  source: "acroform" | "manual" | "ai";  // Extracted from the PDF, manually placed, or added by the AI field audit
  confidence?: number;    // AI detection confidence (0-1)
  fontSize?: number;      // For flat PDF text overlay
  fontFamily?: string;    // For flat PDF text overlay
  lineHeight?: number;    // Leading multiplier (line height ÷ font size). Used for textarea wrapping.
};

// Form templates table
export const formTemplates = pgTable("form_templates", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").references(() => users.id, { onDelete: "cascade" }),
  agency_id: uuid("agency_id").references(() => agencies.id),
  scope: FormTemplateScope("scope").notNull().default("personal"),
  name: text("name").notNull(),
  description: text("description"),

  // PDF storage
  original_pdf_url: text("original_pdf_url").notNull(),  // S3 URL of uploaded PDF
  original_pdf_key: text("original_pdf_key").notNull(),  // S3 key for reference
  is_fillable: boolean("is_fillable").default(false),    // Whether PDF has AcroForm fields

  // Field definitions
  fields: jsonb("fields").$type<FormFieldDefinition[]>().notNull().default([]),
  field_sections: jsonb("field_sections").$type<FormSection[]>().default([]),

  // Output configuration
  output_type: FormOutputType("output_type").default("pdf"),
  text_output_template: text("text_output_template"),    // Template with {{placeholders}} for text output
  text_ai_style: text("text_ai_style"),                  // 'formal', 'brief', 'detailed' for AI-generated text

  is_active: boolean("is_active").default(true),
  // Set while the upload's AI field pass is running. Until this time the
  // template can't be opened, edited, or filled. It expires on its own so a
  // closed tab or a crashed job can never leave a form locked.
  ai_processing_until: timestamp("ai_processing_until", { withTimezone: true }),
  // When an Auto-detect run finished and left suggestions to review (the
  // proposal itself is held in Redis for a day). Stale once the template is
  // edited after this time. Cleared when the suggestions are accepted or dismissed.
  ai_suggestions_at: timestamp("ai_suggestions_at", { withTimezone: true }),
  created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updated_at: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

export type FormTemplate = typeof formTemplates.$inferSelect;
export type NewFormTemplate = typeof formTemplates.$inferInsert;

// Form submissions table - tracks filled form instances
export const formSubmissions = pgTable("form_submissions", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  template_id: uuid("template_id")
    .notNull()
    .references(() => formTemplates.id, { onDelete: "cascade" }),
  case_id: uuid("case_id").references(() => cases.id, { onDelete: "set null" }),  // Optional link to source case

  status: FormSubmissionStatus("status").default("draft"),

  // Filled values
  field_values: jsonb("field_values").$type<Record<string, any>>().default({}),
  ai_confidence: jsonb("ai_confidence").$type<Record<string, number>>().default({}),

  // Generated outputs
  filled_pdf_url: text("filled_pdf_url"),
  filled_pdf_key: text("filled_pdf_key"),
  text_output: text("text_output"),  // For text-based outputs

  // Error tracking
  error: text("error"),

  // Input source tracking
  input_source: text("input_source"),  // 'audio', 'case' (from a case), 'files' (from chosen Files), 'manual'
  input_metadata: jsonb("input_metadata"),  // Source details (e.g., audioUrl, caseId)

  created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updated_at: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

export type FormSubmission = typeof formSubmissions.$inferSelect;
export type NewFormSubmission = typeof formSubmissions.$inferInsert;

// Form submission files - tracks audio files uploaded for form filling
export const formSubmissionFiles = pgTable("form_submission_files", {
  id: uuid("id").primaryKey().defaultRandom(),
  submission_id: uuid("submission_id")
    .notNull()
    .references(() => formSubmissions.id, { onDelete: "cascade" }),

  // File information
  file_url: text("file_url").notNull(),
  file_name: text("file_name"),
  file_size: text("file_size"),
  file_type: text("file_type"),
  media_type: FileMediaType("media_type").notNull(),

  // Processing information
  processing_status: FileProcessingStatus("processing_status").default("pending"),
  error_message: text("error_message"),

  // Audio-specific metadata (reuse AudioFileMetadata type)
  metadata: jsonb("metadata").$type<AudioFileMetadata>(),

  // Display order
  order: integer("order").default(0),

  created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updated_at: timestamp("updated_at", { withTimezone: true }).defaultNow(),
},
(table) => ({
  fileUrlIdx: index("form_submission_files_file_url_idx").on(table.file_url),
}));

export type FormSubmissionFile = typeof formSubmissionFiles.$inferSelect;
export type NewFormSubmissionFile = typeof formSubmissionFiles.$inferInsert;

// ============================================================================
// MFA (Multi-Factor Authentication) for CJIS Compliance
// ============================================================================

// MFA configuration per user or agency admin
export const user_mfa = pgTable("user_mfa", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").references(() => users.id, { onDelete: "cascade" }),
  admin_id: uuid("admin_id").references(() => agency_admins.id, {
    onDelete: "cascade",
  }),
  method: MfaMethod("method").notNull(),

  // TOTP fields
  totp_secret_encrypted: text("totp_secret_encrypted"),
  totp_verified: boolean("totp_verified").default(false),

  // Common
  is_primary: boolean("is_primary").default(false),
  enabled: boolean("enabled").default(true),

  created_at: timestamp("created_at").defaultNow(),
  updated_at: timestamp("updated_at").defaultNow(),
});

export type UserMfa = typeof user_mfa.$inferSelect;
export type NewUserMfa = typeof user_mfa.$inferInsert;

// One-time backup/recovery codes (bcrypt hashed)
export const mfa_backup_codes = pgTable("mfa_backup_codes", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").references(() => users.id, { onDelete: "cascade" }),
  admin_id: uuid("admin_id").references(() => agency_admins.id, {
    onDelete: "cascade",
  }),
  code_hash: varchar("code_hash", { length: 255 }).notNull(),
  used: boolean("used").default(false),
  used_at: timestamp("used_at"),
  created_at: timestamp("created_at").defaultNow(),
});

export type MfaBackupCode = typeof mfa_backup_codes.$inferSelect;
export type NewMfaBackupCode = typeof mfa_backup_codes.$inferInsert;

// Email OTP challenges + rate limiting
export const mfa_challenges = pgTable("mfa_challenges", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").references(() => users.id, { onDelete: "cascade" }),
  admin_id: uuid("admin_id").references(() => agency_admins.id, {
    onDelete: "cascade",
  }),
  method: MfaMethod("method").notNull(),
  code_hash: varchar("code_hash", { length: 255 }).notNull(),
  attempts: integer("attempts").default(0),
  max_attempts: integer("max_attempts").default(5),
  expires_at: timestamp("expires_at").notNull(),
  verified: boolean("verified").default(false),
  created_at: timestamp("created_at").defaultNow(),
});

export type MfaChallenge = typeof mfa_challenges.$inferSelect;
export type NewMfaChallenge = typeof mfa_challenges.$inferInsert;

// Registered WebAuthn authenticators (Touch ID, security keys, etc.)
export const webauthn_credentials = pgTable("webauthn_credentials", {
  id: varchar("id", { length: 512 }).primaryKey(),
  user_id: uuid("user_id").references(() => users.id, { onDelete: "cascade" }),
  admin_id: uuid("admin_id").references(() => agency_admins.id, {
    onDelete: "cascade",
  }),
  public_key: text("public_key").notNull(),
  counter: bigint("counter", { mode: "number" }).notNull().default(0),
  device_type: varchar("device_type", { length: 50 }),
  backed_up: boolean("backed_up").default(false),
  transports: jsonb("transports").$type<string[]>(),
  friendly_name: varchar("friendly_name", { length: 255 }),
  created_at: timestamp("created_at").defaultNow(),
  last_used_at: timestamp("last_used_at"),
});

export type WebAuthnCredential = typeof webauthn_credentials.$inferSelect;
export type NewWebAuthnCredential = typeof webauthn_credentials.$inferInsert;

// Trusted/remembered devices that can skip MFA
export const trusted_devices = pgTable("trusted_devices", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id").references(() => users.id, { onDelete: "cascade" }),
  admin_id: uuid("admin_id").references(() => agency_admins.id, {
    onDelete: "cascade",
  }),
  device_token_hash: varchar("device_token_hash", { length: 255 }).notNull(),
  user_agent: text("user_agent"),
  ip_address: varchar("ip_address", { length: 45 }),
  friendly_name: varchar("friendly_name", { length: 255 }),
  expires_at: timestamp("expires_at").notNull(),
  last_used_at: timestamp("last_used_at"),
  created_at: timestamp("created_at").defaultNow(),
});

export type TrustedDevice = typeof trusted_devices.$inferSelect;
export type NewTrustedDevice = typeof trusted_devices.$inferInsert;

// ============================================================================
// PDF Redactions
// ============================================================================

// Processing state for a redaction document. "processing" while the AI
// identify pass is running; "ready" once redaction areas are stored.
export const RedactionDocumentStatus = pgEnum("redaction_document_status", [
  "uploaded",
  "processing",
  "ready",
  "error",
]);

export type RedactionDocumentStatusType =
  (typeof RedactionDocumentStatus.enumValues)[number];

// A single redaction area anchored to a PDF page. Rects use the same
// coordinate system as FormFieldDefinition.rect: unscaled PDF points with a
// top-left origin, page-relative (page is 1-indexed). One identified PII item
// can produce multiple rects (e.g. a phrase wrapping across lines), so areas
// matched from the same AI item share a groupId.
export type PdfRedactionArea = {
  id: string;
  groupId?: string;        // Links areas produced by the same identified item
  page: number;            // PDF page number (1-indexed)
  rect: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  text?: string;           // The matched text (empty for manually drawn boxes)
  category: RedactionType["category"];
  reason?: string;
  tag?: string;            // Short reader-facing label drawn inside the bar
  tagFontSize?: number;    // Per-redaction label size override (PDF points);
                           // only used when uniform sizing is off
  statute?: string;
  justification?: string;
  footnoteId?: string;     // References a RedactionFootnote on the document;
                           // drives the raised number on the bar + appendix
  source: "ai" | "manual"; // AI-identified vs user-drawn
  enabled: boolean;        // false = unredacted (kept in list, not exported)
};

// A legal-justification footnote for the exported document. Footnotes are
// user-editable and numbered by their position in the document's list;
// redaction areas reference them via footnoteId.
export type RedactionFootnote = {
  id: string;
  statute?: string;        // e.g. "16 M.R.S. §804(3)"
  justification?: string;  // e.g. "Constitutes an invasion of privacy"
};

// Redaction documents table - PDFs uploaded for PII redaction
export const redactionDocuments = pgTable("redaction_documents", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),

  name: text("name").notNull(),

  // PDF storage (shares the form-templates bucket under a redactions/ prefix)
  original_pdf_url: text("original_pdf_url").notNull(),
  original_pdf_key: text("original_pdf_key").notNull(),
  page_count: integer("page_count").default(0),

  status: RedactionDocumentStatus("status").default("uploaded"),
  redactions: jsonb("redactions").$type<PdfRedactionArea[]>().notNull().default([]),
  footnotes: jsonb("footnotes").$type<RedactionFootnote[]>().notNull().default([]),

  // Officer-supplied direction for the AI identify pass, e.g. "only redact
  // minors". Steers `identify-redactions`; reused on Re-identify.
  redaction_instructions: text("redaction_instructions"),

  error: text("error"),

  is_active: boolean("is_active").default(true),
  created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updated_at: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

export type RedactionDocument = typeof redactionDocuments.$inferSelect;
export type NewRedactionDocument = typeof redactionDocuments.$inferInsert;

// ============================================================================
// Investigations — jail-call intelligence for detectives
//
// A separate product surface from Cases. Investigations hold many media
// files (jail calls, PDFs, spreadsheets, photos); every jail call is
// transcribed, translated when needed, and analyzed by an LLM that reads the
// WHOLE call and emits a relevance score, flagged leads, entities, and a
// slang glossary. Chunks are embedded (pgvector) for the investigation
// chatbot. Nothing here touches case_files or the report pipeline.
//
// Constraints on this file still apply: it is copied verbatim to the client
// and mobile bundles, so nothing server-only may be imported.
// ============================================================================

// `tsvector` (the customType used by investigation_chunks.tsv) is declared
// once, above the agency documents tables, so both chunk tables share it.

export const INVESTIGATION_ADDON = "investigations" as const;

export type DeletedFileRecord = {
  fileName: string;
  kind: string;
  deletedAt: string;
  deletedBy: string;
  leadCount: number;
  callLabel?: string | null;
};

export const InvestigationStatus = pgEnum("investigation_status", [
  "active",
  "archived",
]);
export const InvestigationStatusType = z.enum(InvestigationStatus.enumValues);

export const InvestigationMemberRole = pgEnum("investigation_member_role", [
  "owner",
  "member",
]);
export const InvestigationMemberRoleType = z.enum(
  InvestigationMemberRole.enumValues
);

// What pipeline a file takes. A PDF and an XLSX are both media_type
// "document" but are processed completely differently.
export const InvestigationFileKind = pgEnum("investigation_file_kind", [
  "jail_call",
  "document",
  "spreadsheet",
  "photo",
  // Body cam, surveillance, cell-phone, dash cam. Gets the same call row and
  // transcript a jail call does PLUS frame extraction and a visual event log.
  "video",
]);
export const InvestigationFileKindType = z.enum(
  InvestigationFileKind.enumValues
);

export const CallSource = pgEnum("investigation_call_source", [
  "jail",
  "wiretap",
  "interview",
  "other",
]);
export const CallSourceType = z.enum(CallSource.enumValues);

export const CallChannelMode = pgEnum("investigation_call_channel_mode", [
  "channel_identification",
  "diarization",
]);

export const CallReviewStatus = pgEnum("investigation_call_review_status", [
  "unreviewed",
  "in_progress",
  "reviewed",
  "flagged",
  "irrelevant",
]);
export const CallReviewStatusType = z.enum(CallReviewStatus.enumValues);

// Finer than FileProcessingStatus: a call moves through four distinct
// async stages and the UI shows which one it's in.
export const CallPipelineStage = pgEnum("investigation_call_pipeline_stage", [
  "queued",
  "transcribing",
  "translating",
  "analyzing",
  "complete",
  "failed",
]);
export const CallPipelineStageType = z.enum(CallPipelineStage.enumValues);

export const ChunkSourceKind = pgEnum("investigation_chunk_source_kind", [
  "call_segment",
  "document_page",
  "table_summary",
  "image",
  // A visual event from a video's analysis, with exact start_ms/end_ms so a
  // chat citation can seek the player like a call segment does.
  "video_segment",
]);

// Whether a document is the agency's own case material or third-party
// evidence. Official docs (reports, filings, records the officer authored
// or pulled from the case file) are context, not intercepts: authoritative
// for names/aliases/dates, and analyzed for discrepancies and new
// information rather than threat-severity leads. Null for non-documents
// and for files analyzed before this existed.
export const InvestigationDocClass = pgEnum("investigation_doc_class", [
  "official",
  "evidence",
]);
export const InvestigationDocClassType = z.enum(
  InvestigationDocClass.enumValues
);

// Enumerated (not free text) — these are the categories that sell the
// feature and that the Leads queue filters on. The last two are emitted
// only for official case documents: what the doc *changes* (a conflict
// with the calls, or information not yet seen), never a threat flag
// recycled from the officer's own writing.
export const LeadCategory = pgEnum("investigation_lead_category", [
  "violence_threat",
  "witness_tampering",
  "third_party_call",
  "drug_transaction",
  "contraband",
  "money_movement",
  "coded_language",
  "admission",
  "flight_risk",
  "other",
  "discrepancy",
  "report_note",
]);
export const LeadCategoryType = z.enum(LeadCategory.enumValues);

export const LeadStatus = pgEnum("investigation_lead_status", [
  "new",
  "confirmed",
  "dismissed",
]);
export const LeadStatusType = z.enum(LeadStatus.enumValues);

export const LeadSource = pgEnum("investigation_lead_source", ["ai", "manual"]);

export const EntityType = pgEnum("investigation_entity_type", [
  "person",
  "phone",
  "address",
  "org",
  "vehicle",
  "money_account",
  "alias",
  "other",
]);
export const EntityTypeType = z.enum(EntityType.enumValues);

export const InvestigationJobStage = pgEnum("investigation_job_stage", [
  "transcribe",
  "analyze",
  "extract_document",
  "parse_table",
  "describe_photo",
  "rollup",
  // Video files: frames Lambda (runs beside transcribe), then the visual
  // triage/detail/zoom passes plus the structured lead pass once both are in.
  "extract_frames",
  "analyze_video",
]);
export const InvestigationJobStageType = z.enum(
  InvestigationJobStage.enumValues
);

export const InvestigationJobStatus = pgEnum("investigation_job_status", [
  "queued",
  "dispatched",
  "running",
  "complete",
  "failed",
]);
export const InvestigationJobStatusType = z.enum(
  InvestigationJobStatus.enumValues
);

export const AbuseFlagLevel = pgEnum("investigation_abuse_level", [
  "none",
  "watch",
  "throttled",
  "suspended",
]);
export const AbuseFlagLevelType = z.enum(AbuseFlagLevel.enumValues);

// Per-seat add-ons. "video" is the footage tier for case reports (body cam
// analysis, footage check); Investigations includes video for its own files.
export const AddonName = pgEnum("addon_name", ["investigations", "video"]);
export const AddonNameType = z.enum(AddonName.enumValues);

// "invoice" = paid, but sold outside Stripe (contract / PO) and activated
// by hand in /agency-management, like most large deals.
export const AddonSeatsSource = pgEnum("addon_seats_source", [
  "stripe",
  "trial",
  "comp",
  "invoice",
]);

// Hour packs bought on top of the seat pool for one month, plus "seats":
// the seat hours themselves for an annual plan (see getPoolUsage).
export const AddonCreditSource = pgEnum("addon_credit_source", [
  "stripe",
  "invoice",
  "comp",
  "seats",
]);

// Monthly plans reset the allowance every calendar month; annual plans get
// one pool for the 12 months starting with the month the plan began.
export const AddonBillingInterval = pgEnum("addon_billing_interval", ["month", "year"]);
export const AddonBillingIntervalType = z.enum(AddonBillingInterval.enumValues);

// ---- JSON shapes -----------------------------------------------------------

export const INVESTIGATION_TABS = ["brief", "leads", "calls", "people", "timeline"] as const;
export type InvestigationTabKey = (typeof INVESTIGATION_TABS)[number];

export type InvestigationBrief = {
  generatedAt: string;
  model: string;
  headline: string;
  summary: string;
  keyPeople: { name: string; role: string; note?: string }[];
  // Every entry names its source when the model could anchor it: the call,
  // the flagged lead (money/records events), or the document (+page).
  timeline: { date: string; description: string; callId?: string; leadId?: string; fileId?: string; page?: number }[];
  listenFirst: {
    callId: string;
    reason: string;
  }[];
  openQuestions: string[];
  coverage: { callsAnalyzed: number; callsTotal: number };
  // Incremental regeneration: which version this is and what the model says
  // moved since the previous one (new files, revised picks, resolved
  // questions). Absent on the first brief.
  version?: number;
  whatChanged?: string[];
  newSince?: { calls: number; documents: number; previousGeneratedAt: string };
};

// Detective overrides on timeline items, keyed by timelineEntryKey() for
// brief entries ("b:<hash>") or "call:<id>" / "file:<id>" for cards. The
// AI text is never edited or deleted: hide is reversible, the note sits
// beside the entry, and both survive brief regeneration.
export type TimelineOverride = {
  hidden?: boolean;
  note?: string;
  updatedBy?: string; // display name or email
  updatedAt?: string; // ISO
};

export type CallParticipant = {
  label: string; // spk_0 / ch_0
  role?: "inmate" | "outside" | "third_party" | "unknown";
  name?: string;
  // Who asserted the name. Detective-set names survive re-analysis and win
  // when resolving who a dialed number belongs to.
  name_source?: "ai" | "detective";
  // Same-number suggestions the detective rejected ("Not them").
  dismissed_names?: string[];
};

export type GlossaryEntry = {
  term: string;
  literal?: string;
  likelyMeaning: string;
  confidence: number; // 0-1
  quote?: string;
  startMs?: number;
};

export type LanguageSpan = { code: string; durationSeconds: number };

export type CallTopic = { topic: string; weight: number };

export type InvestigationChunkMetadata = {
  sectionHeading?: string;
  sheetName?: string;
  rowRange?: [number, number];
  segmentIds?: number[];
};

export type ChatCitation = {
  n: number;
  kind: "call_segment" | "document_page" | "table_rows" | "entity" | "video_segment";
  fileId?: string;
  callId?: string;
  startMs?: number;
  endMs?: number;
  page?: number;
  // Agency policy citations (/policies chat): fileId is the agency document
  // id and sectionId the section the passage came from.
  sectionId?: string;
  label: string;
  snippet: string;
};

export type ChatTokenUsage = {
  inputTokens: number;
  outputTokens: number;
  cacheReadInputTokens?: number;
  cacheCreationInputTokens?: number;
  iterations?: number;
};

export type AbuseSignals = {
  callsUploaded?: number;
  audioMinutes?: number;
  allowanceCalls?: number;
  allowanceMinutes?: number;
  // "pool" when the officer's agency shares one allowance across its seats
  // (overAllowancePct is then the department's), "personal" for solo seats.
  scope?: "pool" | "personal";
  periodInterval?: "month" | "year";
  periodLabel?: string;
  personalPct?: number;
  poolMinutes?: number;
  poolAllowanceMinutes?: number;
  poolCalls?: number;
  poolAllowanceCalls?: number;
  overAllowancePct?: number;
  simultaneousSessions?: number;
  distinctSubnets24h?: number;
  distinctUserAgents7d?: number;
  activeHoursMax?: number;
  distinctInmates30d?: number;
};

export type TableColumnRoles = {
  date?: string;
  amount?: string;
  counterparty?: string;
  description?: string;
};

// ---- Tables ----------------------------------------------------------------

export const investigations = pgTable("investigations", {
  id: uuid("id").primaryKey().defaultRandom(),
  user_id: uuid("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  agency_id: uuid("agency_id").references(() => agencies.id),
  name: text("name").notNull(),
  case_number: text("case_number"),
  description: text("description"),
  // Free-text steer for the analysis pass: "focus on the Elm St shooting,
  // subjects are D. Lopez and 'Lil D'".
  focus: text("focus"),
  status: InvestigationStatus("status").default("active"),
  brief: jsonb("brief").$type<InvestigationBrief>(),
  brief_generated_at: timestamp("brief_generated_at", { withTimezone: true }),
  // Hard-deleted files, so the Brief tab can flag that the current brief
  // may still reference removed material (entries newer than the brief).
  deleted_files: jsonb("deleted_files").$type<DeletedFileRecord[]>().default([]),
  // Attestation that the uploaded recordings are not attorney-client
  // privileged. Recorded once per investigation; the upload flow blocks
  // until it's set.
  privilege_attested_at: timestamp("privilege_attested_at", {
    withTimezone: true,
  }),
  privilege_attested_by: uuid("privilege_attested_by").references(
    () => users.id
  ),
  // Reserved for a later ingest-time attorney-number blocklist.
  timeline_overrides: jsonb("timeline_overrides").$type<Record<string, TimelineOverride>>().default({}),
  privilege_blocklist: jsonb("privilege_blocklist").$type<string[]>(),
  // Caps chat spend per investigation per day so a runaway agent loop
  // can't burn hundreds of dollars.
  daily_token_budget: integer("daily_token_budget").default(2_000_000),
  is_active: boolean("is_active").default(true),
  created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updated_at: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

export type Investigation = typeof investigations.$inferSelect;

// Every Brief ever generated, oldest first. The live copy on
// `investigations.brief` is always the newest row; prior rows exist so a
// detective can see what changed and so the export carries the full history.
export const investigation_brief_versions = pgTable(
  "investigation_brief_versions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    investigation_id: uuid("investigation_id")
      .notNull()
      .references(() => investigations.id, { onDelete: "cascade" }),
    version: integer("version").notNull(),
    brief: jsonb("brief").$type<InvestigationBrief>().notNull(),
    // What triggered it: files added since the previous version.
    new_call_ids: jsonb("new_call_ids").$type<string[]>().default([]),
    new_file_ids: jsonb("new_file_ids").$type<string[]>().default([]),
    generated_at: timestamp("generated_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    versionUnique: unique("investigation_brief_versions_unique").on(
      table.investigation_id,
      table.version
    ),
  })
);

export type InvestigationBriefVersion =
  typeof investigation_brief_versions.$inferSelect;
export type NewInvestigation = typeof investigations.$inferInsert;

// An investigation is a squad artifact, not a single officer's document.
export const investigation_members = pgTable(
  "investigation_members",
  {
    investigation_id: uuid("investigation_id")
      .notNull()
      .references(() => investigations.id, { onDelete: "cascade" }),
    user_id: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    role: InvestigationMemberRole("role").notNull().default("member"),
    added_by: uuid("added_by").references(() => users.id),
    added_at: timestamp("added_at", { withTimezone: true }).defaultNow(),
    // When this member last cleared each tab's "new since" marker
    // ({ brief, leads, calls, people, timeline } → ISO). Falls back to
    // added_at, so a fresh member sees everything as new once.
    seen_at: jsonb("seen_at").$type<Partial<Record<InvestigationTabKey, string>>>().default({}),
  },
  (table) => ({
    pk: primaryKey({ columns: [table.investigation_id, table.user_id] }),
    userIdx: index("investigation_members_user_idx").on(table.user_id),
  })
);

export type InvestigationMember = typeof investigation_members.$inferSelect;

// Mirrors case_files' shape (same status enum, same jsonb metadata idiom)
// but is its own table: case_files is load-bearing for the report pipeline
// and every cron/coordinator there assumes a case parent.
export const investigation_files = pgTable(
  "investigation_files",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    investigation_id: uuid("investigation_id")
      .notNull()
      .references(() => investigations.id, { onDelete: "cascade" }),
    uploaded_by: uuid("uploaded_by").references(() => users.id),
    kind: InvestigationFileKind("kind").notNull(),
    s3_bucket: text("s3_bucket").notNull(),
    s3_key: text("s3_key").notNull(),
    file_url: text("file_url").notNull(),
    // Sanitized name used for the S3 key, plus the original: with bulk audio
    // ingest the original filename is the ONLY source of call metadata.
    file_name: text("file_name").notNull(),
    original_file_name: text("original_file_name").notNull(),
    file_size: text("file_size"),
    file_size_bytes: bigint("file_size_bytes", { mode: "number" }),
    file_type: text("file_type"),
    media_type: FileMediaType("media_type").notNull(),
    // Computed client-side before the presign so duplicates are refused
    // before any bytes move. Nullable only for legacy/manual inserts.
    sha256: varchar("sha256", { length: 64 }),
    duration_seconds: integer("duration_seconds"),
    processing_status: FileProcessingStatus("processing_status").default(
      "pending"
    ),
    // Set by the S3 ObjectCreated callback, never by the client, so a closed
    // tab between the PUT and any client POST can't strand the file.
    uploaded_at: timestamp("uploaded_at", { withTimezone: true }),
    error_message: text("error_message"),
    metadata: jsonb("metadata").$type<Record<string, any>>(),
    // Documents only. Set by the analysis pass, overridable by the
    // detective; a detective's setting is never overwritten by a re-run.
    source_class: InvestigationDocClass("source_class"),
    // Set when a file is marked privileged after the fact: its chunks are
    // deleted and every tool excludes it.
    privileged_at: timestamp("privileged_at", { withTimezone: true }),
    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
    updated_at: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    investigationIdx: index("investigation_files_investigation_idx").on(
      table.investigation_id
    ),
    shaUnique: unique("investigation_files_sha_unique").on(
      table.investigation_id,
      table.sha256
    ),
  })
);

export type InvestigationFile = typeof investigation_files.$inferSelect;
export type NewInvestigationFile = typeof investigation_files.$inferInsert;

// 1:1 with a jail_call file. "Call" not "jail call": wiretaps and CI
// debriefs go through the same analysis pass.
export const investigation_calls = pgTable(
  "investigation_calls",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    investigation_id: uuid("investigation_id")
      .notNull()
      .references(() => investigations.id, { onDelete: "cascade" }),
    file_id: uuid("file_id")
      .notNull()
      .unique()
      .references(() => investigation_files.id, { onDelete: "cascade" }),
    call_source: CallSource("call_source").notNull().default("jail"),

    // Metadata — filename-parsed in v1, all nullable.
    call_started_at: timestamp("call_started_at", { withTimezone: true }),
    from_number: text("from_number"),
    to_number: text("to_number"),
    inmate_id: text("inmate_id"),
    inmate_name: text("inmate_name"),
    called_party_name: text("called_party_name"),
    facility: text("facility"),

    // Transcription
    transcript_s3_key: text("transcript_s3_key"),
    transcribe_job_name: text("transcribe_job_name"),
    language_codes: jsonb("language_codes").$type<LanguageSpan[]>(),
    primary_language: text("primary_language"),
    channel_mode: CallChannelMode("channel_mode"),
    speaker_count: integer("speaker_count"),
    // Detected from the transcript (automated "this call is from a
    // correctional facility" prompt). Analysis skips everything before it.
    // Audio is NOT trimmed, so timestamps stay in original-audio time.
    preamble_end_ms: integer("preamble_end_ms"),
    duration_ms: integer("duration_ms"),

    // Translation (only when primary language isn't English)
    translated_transcript_s3_key: text("translated_transcript_s3_key"),
    translation_target: text("translation_target"),

    // Analysis
    relevance_score: integer("relevance_score"),
    relevance_reason: text("relevance_reason"),
    summary: text("summary"),
    topics: jsonb("topics").$type<CallTopic[]>(),
    participants: jsonb("participants").$type<CallParticipant[]>(),
    glossary: jsonb("glossary").$type<GlossaryEntry[]>(),
    has_third_party: boolean("has_third_party").default(false),
    analysis_model: text("analysis_model"),
    // Bumped when the prompt changes so "re-score all 50" is an idempotent
    // upsert rather than a duplicate explosion.
    analysis_version: integer("analysis_version").default(0),
    analyzed_at: timestamp("analyzed_at", { withTimezone: true }),

    // Review
    review_status: CallReviewStatus("review_status")
      .notNull()
      .default("unreviewed"),
    reviewed_by: uuid("reviewed_by").references(() => users.id),
    reviewed_at: timestamp("reviewed_at", { withTimezone: true }),
    analyst_notes: text("analyst_notes"),
    is_starred: boolean("is_starred").default(false),

    // Pipeline
    pipeline_stage: CallPipelineStage("pipeline_stage")
      .notNull()
      .default("queued"),
    error_message: text("error_message"),

    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
    updated_at: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    relevanceIdx: index("investigation_calls_relevance_idx").on(
      table.investigation_id,
      table.relevance_score
    ),
  })
);

export type InvestigationCall = typeof investigation_calls.$inferSelect;
export type NewInvestigationCall = typeof investigation_calls.$inferInsert;

// The retrieval surface. English-only embeddings (content), verbatim native
// text alongside (content_native). No HNSW index on purpose: a single global
// HNSW with `WHERE investigation_id = ?` post-filters and loses recall, and
// at ~650 chunks per investigation an exact scan is faster anyway. Revisit
// past ~500k rows.
export const investigation_chunks = pgTable(
  "investigation_chunks",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    investigation_id: uuid("investigation_id")
      .notNull()
      .references(() => investigations.id, { onDelete: "cascade" }),
    file_id: uuid("file_id")
      .notNull()
      .references(() => investigation_files.id, { onDelete: "cascade" }),
    call_id: uuid("call_id").references(() => investigation_calls.id, {
      onDelete: "cascade",
    }),
    chunk_index: integer("chunk_index").notNull(),
    analysis_version: integer("analysis_version").default(0),
    source_kind: ChunkSourceKind("source_kind").notNull(),
    content: text("content").notNull(),
    content_native: text("content_native"),
    lang: text("lang"),
    embedding: vector("embedding", { dimensions: 1024 }),
    tsv: tsvector("tsv").generatedAlwaysAs(
      sql`to_tsvector('simple', coalesce(content, ''))`
    ),
    start_ms: integer("start_ms"),
    end_ms: integer("end_ms"),
    speaker_label: text("speaker_label"),
    page_start: integer("page_start"),
    page_end: integer("page_end"),
    metadata: jsonb("metadata").$type<InvestigationChunkMetadata>(),
    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    investigationIdx: index("investigation_chunks_investigation_idx").on(
      table.investigation_id
    ),
    fileIdx: index("investigation_chunks_file_idx").on(table.file_id),
    tsvIdx: index("investigation_chunks_tsv_idx").using("gin", table.tsv),
  })
);

export type InvestigationChunk = typeof investigation_chunks.$inferSelect;

// Spreadsheet leads only: which sheet/rows/column the finding rests on, so
// the viewer can highlight them like flagged transcript segments. Rows are
// row_index values resolved server-side at analysis time (model row refs
// plus counterparty matches); match records why they matched, for display.
export type SpreadsheetLeadAnchor = {
  sheet: string;
  tableId?: string;
  rows?: number[];
  column?: string;
  match?: { column: string; value: string };
};

// Document (PDF) leads only: the quote's Textract line boxes, so the PDF
// viewer can paint the highlight. Coordinates are normalized 0-1 page
// fractions straight from Textract, which makes scanned and native PDFs
// identical. Resolved server-side at analysis time; null when the quote
// couldn't be located (the lead still page-jumps).
export type DocLeadAnchor = {
  page: number;
  boxes: { x: number; y: number; w: number; h: number }[];
};

// Video leads only: the moment the finding rests on and the stills that
// support it, so the video view can drop a marker on the bar, seek to it,
// and show the frame. frame_ts are seconds into the video; region is an
// optional normalized 0-1 box within the frame at frame_ts[0].
export type VideoLeadAnchor = {
  t_start_ms: number;
  t_end_ms: number;
  frame_ts: number[];
  kind?: string; // VideoEventKind the lead was derived from
  region?: { x: number; y: number; w: number; h: number };
};

// The flagged-moment queue. Every lead is anchored to a timestamp or page
// so the quote is checkable in one click.
export const investigation_leads = pgTable(
  "investigation_leads",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    investigation_id: uuid("investigation_id")
      .notNull()
      .references(() => investigations.id, { onDelete: "cascade" }),
    file_id: uuid("file_id")
      .notNull()
      .references(() => investigation_files.id, { onDelete: "cascade" }),
    call_id: uuid("call_id").references(() => investigation_calls.id, {
      onDelete: "cascade",
    }),
    category: LeadCategory("category").notNull(),
    severity: integer("severity").notNull().default(3), // 1-5
    confidence: decimal("confidence", { precision: 3, scale: 2 }),
    quote: text("quote").notNull(),
    quote_translated: text("quote_translated"),
    start_ms: integer("start_ms"),
    end_ms: integer("end_ms"),
    speaker_label: text("speaker_label"),
    page: integer("page"),
    sheet_anchor: jsonb("sheet_anchor").$type<SpreadsheetLeadAnchor | null>(),
    doc_anchor: jsonb("doc_anchor").$type<DocLeadAnchor | null>(),
    video_anchor: jsonb("video_anchor").$type<VideoLeadAnchor | null>(),
    explanation: text("explanation"),
    status: LeadStatus("status").notNull().default("new"),
    // Why a lead was dismissed. "already_known" is the one the DA wants to
    // see; "ai_misread" is a false positive; "not_relevant" is judgment.
    dismiss_reason: text("dismiss_reason"),
    status_changed_by: uuid("status_changed_by").references(() => users.id),
    status_changed_at: timestamp("status_changed_at", { withTimezone: true }),
    note: text("note"),
    source: LeadSource("source").notNull().default("ai"),
    analysis_version: integer("analysis_version").default(0),
    // hash(file_id + start_ms bucketed to 5s + category). The unique index
    // turns a re-run into an upsert.
    dedupe_key: varchar("dedupe_key", { length: 64 }).notNull(),
    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
    updated_at: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    dedupeUnique: unique("investigation_leads_dedupe_unique").on(
      table.dedupe_key
    ),
    investigationIdx: index("investigation_leads_investigation_idx").on(
      table.investigation_id,
      table.status
    ),
  })
);

export type InvestigationLead = typeof investigation_leads.$inferSelect;
export type NewInvestigationLead = typeof investigation_leads.$inferInsert;

export const investigation_entities = pgTable(
  "investigation_entities",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    investigation_id: uuid("investigation_id")
      .notNull()
      .references(() => investigations.id, { onDelete: "cascade" }),
    type: EntityType("type").notNull(),
    canonical_name: text("canonical_name").notNull(),
    normalized_name: text("normalized_name").notNull(),
    aliases: jsonb("aliases").$type<string[]>().default([]),
    mention_count: integer("mention_count").default(0),
    notes: text("notes"),
    // The LLM will emit "Lil D", "Little D", and "Dominic" as three
    // entities; a merge button plus this pointer is the cheap fix.
    merged_into_id: uuid("merged_into_id"),
    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
    updated_at: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    investigationIdx: index("investigation_entities_investigation_idx").on(
      table.investigation_id
    ),
    nameUnique: unique("investigation_entities_name_unique").on(
      table.investigation_id,
      table.type,
      table.normalized_name
    ),
  })
);

export type InvestigationEntity = typeof investigation_entities.$inferSelect;

export const investigation_entity_mentions = pgTable(
  "investigation_entity_mentions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    entity_id: uuid("entity_id")
      .notNull()
      .references(() => investigation_entities.id, { onDelete: "cascade" }),
    file_id: uuid("file_id")
      .notNull()
      .references(() => investigation_files.id, { onDelete: "cascade" }),
    call_id: uuid("call_id").references(() => investigation_calls.id, {
      onDelete: "cascade",
    }),
    start_ms: integer("start_ms"),
    page: integer("page"),
    surface_form: text("surface_form"),
    context_snippet: text("context_snippet"),
    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    entityIdx: index("investigation_entity_mentions_entity_idx").on(
      table.entity_id
    ),
  })
);

export type InvestigationEntityMention =
  typeof investigation_entity_mentions.$inferSelect;

// Spreadsheets are never embedded row-by-row. Rows live here with promoted
// typed columns; the chat gets a constrained query tool over them.
export const investigation_tables = pgTable("investigation_tables", {
  id: uuid("id").primaryKey().defaultRandom(),
  investigation_id: uuid("investigation_id")
    .notNull()
    .references(() => investigations.id, { onDelete: "cascade" }),
  file_id: uuid("file_id")
    .notNull()
    .references(() => investigation_files.id, { onDelete: "cascade" }),
  sheet_name: text("sheet_name").notNull(),
  header_row: jsonb("header_row").$type<string[]>().notNull(),
  row_count: integer("row_count").default(0),
  column_types: jsonb("column_types").$type<Record<string, string>>(),
  column_roles: jsonb("column_roles").$type<TableColumnRoles>(),
  summary: text("summary"),
  created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
});

export type InvestigationTable = typeof investigation_tables.$inferSelect;

export const investigation_table_rows = pgTable(
  "investigation_table_rows",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    table_id: uuid("table_id")
      .notNull()
      .references(() => investigation_tables.id, { onDelete: "cascade" }),
    row_index: integer("row_index").notNull(),
    data: jsonb("data").$type<Record<string, string | number | null>>().notNull(),
    row_date: timestamp("row_date", { withTimezone: true }),
    amount: decimal("amount", { precision: 14, scale: 2 }),
    counterparty: text("counterparty"),
  },
  (table) => ({
    tableIdx: index("investigation_table_rows_table_idx").on(
      table.table_id,
      table.row_index
    ),
    dateIdx: index("investigation_table_rows_date_idx").on(
      table.table_id,
      table.row_date
    ),
    counterpartyIdx: index("investigation_table_rows_counterparty_idx").on(
      table.table_id,
      table.counterparty
    ),
    dataIdx: index("investigation_table_rows_data_idx").using(
      "gin",
      table.data.op("jsonb_path_ops")
    ),
  })
);

export type InvestigationTableRow = typeof investigation_table_rows.$inferSelect;

export const investigation_chat_threads = pgTable(
  "investigation_chat_threads",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    investigation_id: uuid("investigation_id")
      .notNull()
      .references(() => investigations.id, { onDelete: "cascade" }),
    user_id: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    title: text("title"),
    // Set when the chat was opened from a call: the assistant prioritizes
    // that call's transcript but can still consult the rest of the case.
    focus_call_id: uuid("focus_call_id").references(() => investigation_calls.id, { onDelete: "set null" }),
    // Citation numbering is global per thread (see investigationChatService)
    // so "[3]" always means the same passage for the life of the thread.
    citation_counter: integer("citation_counter").default(0),
    last_message_at: timestamp("last_message_at", { withTimezone: true }),
    archived_at: timestamp("archived_at", { withTimezone: true }),
    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    investigationIdx: index("investigation_chat_threads_investigation_idx").on(
      table.investigation_id,
      table.user_id
    ),
  })
);

export type InvestigationChatThread =
  typeof investigation_chat_threads.$inferSelect;

export const investigation_chat_messages = pgTable(
  "investigation_chat_messages",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    thread_id: uuid("thread_id")
      .notNull()
      .references(() => investigation_chat_threads.id, {
        onDelete: "cascade",
      }),
    role: text("role").notNull(), // "user" | "assistant"
    content: text("content").notNull(),
    // Raw Anthropic content-block array including tool_use/tool_result.
    // Persisting this verbatim is what lets the server rebuild a
    // byte-identical prefix for prompt caching, and it's the audit trail of
    // what the assistant read before it answered.
    blocks: jsonb("blocks").$type<any[]>(),
    citations: jsonb("citations").$type<ChatCitation[]>(),
    token_usage: jsonb("token_usage").$type<ChatTokenUsage>(),
    model: text("model"),
    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    threadIdx: index("investigation_chat_messages_thread_idx").on(
      table.thread_id,
      table.created_at
    ),
  })
);

export type InvestigationChatMessage =
  typeof investigation_chat_messages.$inferSelect;

// Durable work queue. The dispatcher cron claims rows with
// FOR UPDATE SKIP LOCKED, enforces per-stage concurrency ceilings, and
// reaps stuck rows. Lambdas are never fired directly from a request.
export const investigation_jobs = pgTable(
  "investigation_jobs",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    investigation_id: uuid("investigation_id")
      .notNull()
      .references(() => investigations.id, { onDelete: "cascade" }),
    file_id: uuid("file_id").references(() => investigation_files.id, {
      onDelete: "cascade",
    }),
    stage: InvestigationJobStage("stage").notNull(),
    status: InvestigationJobStatus("status").notNull().default("queued"),
    attempts: integer("attempts").default(0),
    max_attempts: integer("max_attempts").default(3),
    priority: integer("priority").default(10),
    // Transcribe job name, deterministic: inv-{fileId}-a{attempt}. Lets the
    // reaper settle FAILED jobs via GetTranscriptionJob and makes retries
    // idempotent.
    external_job_name: text("external_job_name"),
    payload: jsonb("payload").$type<Record<string, any>>(),
    dispatched_at: timestamp("dispatched_at", { withTimezone: true }),
    started_at: timestamp("started_at", { withTimezone: true }),
    finished_at: timestamp("finished_at", { withTimezone: true }),
    next_attempt_at: timestamp("next_attempt_at", { withTimezone: true }),
    error_message: text("error_message"),
    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
    updated_at: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    fileStageUnique: unique("investigation_jobs_file_stage_unique").on(
      table.file_id,
      table.stage
    ),
    queueIdx: index("investigation_jobs_queue_idx").on(
      table.status,
      table.stage,
      table.priority
    ),
  })
);

export type InvestigationJob = typeof investigation_jobs.$inferSelect;
export type NewInvestigationJob = typeof investigation_jobs.$inferInsert;

// ---- Entitlement -----------------------------------------------------------

export const agency_addon_seats = pgTable(
  "agency_addon_seats",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    agency_id: uuid("agency_id")
      .notNull()
      .references(() => agencies.id, { onDelete: "cascade" }),
    addon: AddonName("addon").notNull(),
    seats_purchased: integer("seats_purchased").notNull().default(0),
    seats_source: AddonSeatsSource("seats_source").notNull().default("stripe"),
    stripe_subscription_id: text("stripe_subscription_id"),
    stripe_subscription_item_id: text("stripe_subscription_item_id"),
    status: SubscriptionStatus("status").default("active"),
    billing_interval: AddonBillingInterval("billing_interval").notNull().default("month"),
    current_period_start: timestamp("current_period_start", { withTimezone: true }),
    current_period_end: timestamp("current_period_end", { withTimezone: true }),
    // Highest pool-usage threshold (80/100/150) already emailed to the
    // agency's admins, keyed by allowance period, so each fires once a period.
    pool_alerts: jsonb("pool_alerts").$type<Record<string, number>>().default({}),
    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
    updated_at: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    agencyAddonUnique: unique("agency_addon_seats_agency_addon_unique").on(
      table.agency_id,
      table.addon
    ),
  })
);

export type AgencyAddonSeats = typeof agency_addon_seats.$inferSelect;

// Which officers hold a seat. Without this, a per-seat add-on any of 200
// officers can use is a one-seat add-on.
export const user_addon_grants = pgTable(
  "user_addon_grants",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    user_id: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    addon: AddonName("addon").notNull(),
    // Individual (non-agency) purchases carry their own Stripe subscription.
    stripe_subscription_id: text("stripe_subscription_id"),
    billing_interval: AddonBillingInterval("billing_interval").notNull().default("month"),
    current_period_start: timestamp("current_period_start", { withTimezone: true }),
    current_period_end: timestamp("current_period_end", { withTimezone: true }),
    // Per-user override of the monthly allowance (null = plan default).
    allowance_calls: integer("allowance_calls"),
    allowance_minutes: integer("allowance_minutes"),
    granted_by: uuid("granted_by"),
    granted_at: timestamp("granted_at", { withTimezone: true }).defaultNow(),
    revoked_at: timestamp("revoked_at", { withTimezone: true }),
  },
  (table) => ({
    userAddonUnique: unique("user_addon_grants_user_addon_unique").on(
      table.user_id,
      table.addon
    ),
  })
);

export type UserAddonGrant = typeof user_addon_grants.$inferSelect;

// The agency pool ledger. Hour packs are one row per purchase for one
// month. On an annual plan the seat hours are rows too (source "seats":
// a base row for the period plus a prorated delta whenever the seat count
// changes), so an annual pool is simply the sum of its period's rows.
// Never rolls over.
export const agency_addon_credits = pgTable(
  "agency_addon_credits",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    agency_id: uuid("agency_id")
      .notNull()
      .references(() => agencies.id, { onDelete: "cascade" }),
    addon: AddonName("addon").notNull(),
    period_month: varchar("period_month", { length: 7 }).notNull(), // YYYY-MM
    minutes: integer("minutes").notNull(),
    source: AddonCreditSource("source").notNull().default("stripe"),
    // Stripe Checkout session id; unique so a retried webhook can't double-credit.
    stripe_checkout_session_id: text("stripe_checkout_session_id").unique(),
    // Idempotency key for seat rows (period base / seat-change delta).
    dedupe_key: text("dedupe_key").unique(),
    seats_delta: integer("seats_delta"),
    granted_by: text("granted_by"),
    note: text("note"),
    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    agencyMonthIdx: index("agency_addon_credits_agency_month_idx").on(
      table.agency_id,
      table.addon,
      table.period_month
    ),
  })
);

export type AgencyAddonCredit = typeof agency_addon_credits.$inferSelect;

// ---- Abuse detection -------------------------------------------------------

// Per-user per-month counters. The seat-sharing tell is several distinct
// sessions uploading in the same window, not raw volume.
export const investigation_usage_counters = pgTable(
  "investigation_usage_counters",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    user_id: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    agency_id: uuid("agency_id").references(() => agencies.id),
    period_month: varchar("period_month", { length: 7 }).notNull(), // YYYY-MM
    calls_uploaded: integer("calls_uploaded").default(0),
    audio_minutes: integer("audio_minutes").default(0),
    // Minutes of footage sent through the visual passes. Counted against the
    // pool at INVESTIGATION_VIDEO_MINUTE_WEIGHT (default 3) per minute since
    // frame analysis costs roughly 3x a transcribed minute.
    video_minutes: integer("video_minutes").default(0),
    // Minutes of case (report) footage analyzed: the "video" add-on's own
    // pool, separate from the Investigations pool above.
    case_video_minutes: integer("case_video_minutes").default(0),
    files_uploaded: integer("files_uploaded").default(0),
    investigations_created: integer("investigations_created").default(0),
    chat_messages: integer("chat_messages").default(0),
    tokens_in: bigint("tokens_in", { mode: "number" }).default(0),
    tokens_out: bigint("tokens_out", { mode: "number" }).default(0),
    updated_at: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    userMonthUnique: unique("investigation_usage_user_month_unique").on(
      table.user_id,
      table.period_month
    ),
  })
);

export type InvestigationUsageCounter =
  typeof investigation_usage_counters.$inferSelect;

// One row per ingest event; the abuse cron windows over these.
export const investigation_ingest_events = pgTable(
  "investigation_ingest_events",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    user_id: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    investigation_id: uuid("investigation_id").references(
      () => investigations.id,
      { onDelete: "set null" }
    ),
    session_id: varchar("session_id", { length: 128 }),
    ip_subnet: varchar("ip_subnet", { length: 64 }),
    user_agent_hash: varchar("user_agent_hash", { length: 64 }),
    inmate_id: text("inmate_id"),
    file_count: integer("file_count").default(1),
    audio_minutes: integer("audio_minutes").default(0),
    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    userTimeIdx: index("investigation_ingest_events_user_time_idx").on(
      table.user_id,
      table.created_at
    ),
  })
);

export const investigation_abuse_flags = pgTable(
  "investigation_abuse_flags",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    user_id: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    agency_id: uuid("agency_id").references(() => agencies.id),
    level: AbuseFlagLevel("level").notNull().default("none"),
    reason: text("reason"),
    signals: jsonb("signals").$type<AbuseSignals>(),
    auto_flagged: boolean("auto_flagged").default(true),
    flagged_at: timestamp("flagged_at", { withTimezone: true }).defaultNow(),
    cleared_at: timestamp("cleared_at", { withTimezone: true }),
    cleared_by: varchar("cleared_by", { length: 255 }),
    notes: text("notes"),
    updated_at: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    userIdx: index("investigation_abuse_flags_user_idx").on(
      table.user_id,
      table.cleared_at
    ),
  })
);

export type InvestigationAbuseFlag = typeof investigation_abuse_flags.$inferSelect;

// ============================================================================
// Feature Requests (Canny-style board): officers post, upvote, and comment;
// staff (SUPER_ADMIN_EMAILS) set status. One global board across agencies.
// ============================================================================

export const FeatureRequestStatus = pgEnum("feature_request_status", [
  "open",
  "under_review",
  "planned",
  "in_progress",
  "delivered",
  "declined",
]);
export const FeatureRequestStatusType = z.enum(FeatureRequestStatus.enumValues);

export const FeatureRequestCategory = pgEnum("feature_request_category", [
  "reports",
  "editor",
  "forms",
  "redactions",
  "investigations",
  "mobile",
  "integrations",
  "other",
]);
export const FeatureRequestCategoryType = z.enum(
  FeatureRequestCategory.enumValues
);

export const feature_requests = pgTable(
  "feature_requests",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    // Author. Nullable + set null so the post outlives account deletion.
    user_id: uuid("user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    // Denormalized at post time for the byline and per-agency reporting.
    agency_id: uuid("agency_id").references(() => agencies.id, {
      onDelete: "set null",
    }),
    title: varchar("title", { length: 140 }).notNull(),
    body: text("body").notNull(),
    category: FeatureRequestCategory("category").notNull().default("other"),
    status: FeatureRequestStatus("status").notNull().default("open"),
    status_changed_at: timestamp("status_changed_at", { withTimezone: true }),
    // Pinned official reply shown above the comments.
    staff_response: text("staff_response"),
    // Shown in the changelog once delivered.
    release_note: text("release_note"),
    delivered_at: timestamp("delivered_at", { withTimezone: true }),
    // Duplicate folded into another request: hidden, votes moved over.
    merged_into_id: uuid("merged_into_id"),
    hidden_at: timestamp("hidden_at", { withTimezone: true }),
    hidden_by: varchar("hidden_by", { length: 255 }),
    // Denormalized counters, recomputed from the join tables on every change.
    vote_count: integer("vote_count").notNull().default(0),
    comment_count: integer("comment_count").notNull().default(0),
    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
    updated_at: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    statusIdx: index("feature_requests_status_idx").on(table.status),
    votesIdx: index("feature_requests_votes_idx").on(table.vote_count),
    createdIdx: index("feature_requests_created_idx").on(table.created_at),
  })
);

export type FeatureRequest = typeof feature_requests.$inferSelect;
export type NewFeatureRequest = typeof feature_requests.$inferInsert;

export const feature_request_votes = pgTable(
  "feature_request_votes",
  {
    feature_request_id: uuid("feature_request_id")
      .notNull()
      .references(() => feature_requests.id, { onDelete: "cascade" }),
    user_id: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    pk: primaryKey({ columns: [table.feature_request_id, table.user_id] }),
  })
);

export type FeatureRequestVote = typeof feature_request_votes.$inferSelect;

export const feature_request_comments = pgTable(
  "feature_request_comments",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    feature_request_id: uuid("feature_request_id")
      .notNull()
      .references(() => feature_requests.id, { onDelete: "cascade" }),
    user_id: uuid("user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    // Single-level threading: set on a reply, always pointing at a
    // top-level comment (the server refuses replies to replies).
    parent_id: uuid("parent_id").references(
      (): AnyPgColumn => feature_request_comments.id,
      { onDelete: "cascade" }
    ),
    body: text("body").notNull(),
    // Snapshot of "was the author staff when this was posted".
    is_staff: boolean("is_staff").notNull().default(false),
    hidden_at: timestamp("hidden_at", { withTimezone: true }),
    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
    updated_at: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    requestIdx: index("feature_request_comments_request_idx").on(
      table.feature_request_id,
      table.created_at
    ),
  })
);

export type FeatureRequestComment = typeof feature_request_comments.$inferSelect;

// One row per (user, terms version): a legal record of acceptance. Bumping
// FEATURE_REQUEST_TERMS_VERSION re-prompts everyone.
export const feature_request_terms_acceptances = pgTable(
  "feature_request_terms_acceptances",
  {
    user_id: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    version: integer("version").notNull(),
    accepted_at: timestamp("accepted_at", { withTimezone: true }).defaultNow(),
    ip_address: varchar("ip_address", { length: 45 }),
    user_agent: text("user_agent"),
  },
  (table) => ({
    pk: primaryKey({ columns: [table.user_id, table.version] }),
  })
);

export type FeatureRequestTermsAcceptance =
  typeof feature_request_terms_acceptances.$inferSelect;

// Thread subscriptions. The subscriber set is DERIVED: the author, every
// upvoter, and every commenter are subscribed by default; this table holds
// only explicit overrides (muted = true for "unsubscribe", muted = false
// for a user who opted in without voting or commenting).
export const feature_request_subscriptions = pgTable(
  "feature_request_subscriptions",
  {
    feature_request_id: uuid("feature_request_id")
      .notNull()
      .references(() => feature_requests.id, { onDelete: "cascade" }),
    user_id: uuid("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    muted: boolean("muted").notNull().default(false),
    created_at: timestamp("created_at", { withTimezone: true }).defaultNow(),
    updated_at: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    pk: primaryKey({ columns: [table.feature_request_id, table.user_id] }),
  })
);

export type FeatureRequestSubscription =
  typeof feature_request_subscriptions.$inferSelect;
